/* Ayudate · Hoja de estilos
   Todo el sistema visual en un solo lugar. Los colores viven en variables:
   cambiar la identidad de una página es cambiar seis valores.              */

export const ESTILOS = `
@import url('https://fonts.googleapis.com/css2?family=Atkinson+Hyperlegible:wght@400;700&family=Outfit:wght@300;400;500;600&family=Inter:wght@400;450;500;600&family=IBM+Plex+Mono:wght@400;450;500&display=swap');

/* ============================================================
   SISTEMA DE COLOR — cian + turquesa sobre base oscura
   Tokens semánticos: superficie, borde, texto, acento, estado.
   ============================================================ */
.v2{
  color-scheme:dark;

  /* superficies */
  --bg:#09090B;
  --bg-2:#0E0E11;
  --surface:#141417;
  --surface-2:#1A1A1E;
  --surface-3:#212126;

  /* bordes */
  --border:rgba(255,255,255,.09);
  --border-strong:rgba(255,255,255,.15);
  --border-accent:rgba(20,184,166,.42);

  /* texto · --text-3 se aclaró de #64748B a #7C8B9C para pasar 4,5:1 */
  --text-1:#F8FAFC;
  --text-2:#9AA9BA;
  --text-3:#7C8B9C;
  --text-inv:#04211F;

  /* acento · plano, sin degradados */
  --accent:#14B8A6;        /* turquesa, primario */
  --accent-2:#06B6D4;      /* cian, secundario */
  --accent-hi:#67E8F9;     /* realce */
  --accent-hover:#2DD4BF;
  --accent-deep:#0D9488;
  --accent-soft:rgba(20,184,166,.09);
  --accent-line:rgba(20,184,166,.28);
  /* --grad y --grad-hover se conservan como TOKEN, no como degradado: eran
     el relleno de botones, chips y barras por toda la hoja, y ahora los
     resuelve un color plano. Se dejan con el nombre viejo a propósito —
     renombrarlos en sesenta reglas no cambia un pixel del resultado. Lo
     único que sí hubo que tocar son los cuatro sitios que los recortaban
     sobre el texto (background-clip:text): con un color plano, eso deja el
     texto invisible. */
  --grad:var(--accent);
  --grad-hover:var(--accent-hover);

  /* disponibilidad: tutorías estrictamente virtuales, no cambian entre páginas */
  --mod-off:#000000;       /* no disponible · negro */
  --mod-virt:#3B82F6;      /* virtual · azul */

  /* estados */
  --success:#2DD4BF;
  --warning:#E0B15C;
  --error:#F87171;

  /* elevación neutra, nunca teñida de acento: una sombra turquesa bajo cada
     tarjeta es de lo que más delata una página generada. */
  --glow-sm:0 1px 2px rgba(0,0,0,.5);
  --glow-md:0 10px 28px -14px rgba(0,0,0,.8);
  --glow-lg:0 28px 64px -24px rgba(0,0,0,.85);

  /* compatibilidad heredada */
  --card:var(--surface-2); --edge:var(--border); --fg:var(--text-1);
  --dim:var(--text-2); --hot:var(--accent); --bg2:var(--bg-2);
  --surface-inv:var(--accent);

  /* Escala de radios de tres pasos (6/10/14) en vez del 999 px de antes en
     todo. --r-pill sobrevive solo donde una píldora es lo correcto: las
     barras de progreso y los chips de estado. */
  --r-sm:6px; --r-md:10px; --r-lg:14px; --r-pill:999px;

  /* Curvas y duraciones, en un solo lugar. Las de CSS vienen flojas: estas
     son las que usa el sistema. Toda transición de la hoja nombra sus
     propiedades y sale de acá. */
  --ease:cubic-bezier(.2,.6,.35,1);
  --ease-out:cubic-bezier(.23,1,.32,1);
  --t-rapido:.14s;    /* hover, color, foco */
  --t-medio:.2s;      /* acordeones, menús, capas */
  --t-entrada:.42s;   /* la entrada del encabezado, una sola vez por carga */
  /* Casi todo lo que reacciona al puntero cambia lo mismo: superficie,
     borde y color de texto. Va como lista completa en un token para no
     escribir tres propiedades en cada regla ni caer en «transition:all»,
     que anima también el ancho y el alto y frena la página. */
  --tr-superficie:background-color .14s var(--ease), border-color .14s var(--ease),
                  color .14s var(--ease);

  background:var(--bg); color:var(--text-1); min-height:100vh;
  font-family:'Inter',system-ui,sans-serif; font-size:16px; line-height:1.6;
  -webkit-font-smoothing:antialiased; position:relative; overflow:visible;
  -webkit-tap-highlight-color:rgba(20,184,166,.18);
  padding-left:env(safe-area-inset-left); padding-right:env(safe-area-inset-right);
}
.v2 > *{position:relative; z-index:1;}
.v2 *{box-sizing:border-box;}
.v2 ::selection{background:rgba(20,184,166,.28); color:var(--text-1);}
.v2 :focus-visible{outline:2px solid var(--accent-hi); outline-offset:3px; border-radius:4px;}
.wrap{max-width:1180px; margin:0 auto; padding:0 24px;}
.mono{font-family:'IBM Plex Mono',ui-monospace,monospace; font-variant-numeric:tabular-nums;}
.num{font-variant-numeric:tabular-nums;}
.anton{font-family:'Outfit','Inter',sans-serif; font-weight:400; letter-spacing:-.028em;
  line-height:1.1; text-transform:none; text-wrap:balance;}
/* Etiqueta de sección: mono pequeña y apagada, sin píldora ni puntito. Es
   una referencia, no un titular — el titular va justo debajo. */
.eye{font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:.14em;
  text-transform:uppercase; color:var(--text-3); display:block; margin-bottom:14px;}

/* ---------- NAV ---------- */
.nav{position:fixed; top:0; left:0; right:0; z-index:60; background:var(--bg);
  border-bottom:1px solid var(--border);}
.contenido{padding-top:64px;}
.nav-in{display:flex; align-items:center; justify-content:space-between; height:64px; gap:20px;}

/* Logotipo: el isotipo y la palabra al lado (ver componentes/Marca.jsx).
   La palabra va entera y de un solo color; el turquesa vive en el símbolo.
   Todo se mide en em contra .logo, así que cambiar font-size acá reescala
   el conjunto —símbolo, hueco y palabra— sin tocar nada más: la barra lo
   usa a 18 px y los paneles a 20. */
.logo{display:inline-flex; align-items:baseline; gap:.256em; font-family:'Outfit',sans-serif;
  font-weight:600; font-size:18px; letter-spacing:-.01em; color:var(--text-1);
  text-transform:none; flex:0 0 auto;}
/* Proporciones medidas sobre el manual (logos_y_estilos/ayudate-logo-*.png):
   ahí el símbolo mide 1,551 veces la altura de la A y el hueco 0,360 de esa
   misma altura. La A de Outfit 600 mide 0,71 em, así que:
     alto  = 1,551 × 0,71 = 1,1 em
     hueco = 0,360 × 0,71 = 0,256 em
   El símbolo se apoya en la línea de base y baja 0,195 em, que es lo que
   deja su centro a la altura del centro de la A (0,355 em sobre la línea):
   así está alineado en el manual, y no centrado contra el renglón entero,
   que lo subiría por culpa del rabo de la "y". Como transform no ocupa
   espacio, ese descenso cae dentro del que ya reserva la propia "y".
   El viewBox del símbolo empieza en 2 2 y no en 0 0 justamente para que
   esta altura sea la del cuadro y no la del aire que lo rodea. */
.logo .iso{flex:0 0 auto; height:1.1em; width:auto; display:block;
  transform:translateY(.195em);}
.logo .marca{flex:0 0 auto;}
/* En la barra el logotipo es un enlace (vuelve al inicio) y tiene que
   avisar que se puede tocar. El cuadro turquesa que había antes lo hacía
   aclarándose; el símbolo de ahora es una pieza de marca con sus propios
   colores, así que lo que baja es la opacidad del conjunto — no se
   repinta nada. Solo donde hay puntero de verdad: en una pantalla táctil
   el estado :hover se queda pegado después de tocar. */
@media (hover:hover) and (pointer:fine){
  a.logo{transition:opacity var(--t-rapido) var(--ease);}
  a.logo:hover{opacity:.78;}
}

.nav-l{display:flex; gap:2px; font-size:14px; font-weight:450; min-width:0;}
.nav-l a{color:var(--text-2); text-decoration:none; padding:6px 11px; border-radius:var(--r-sm);
  white-space:nowrap; touch-action:manipulation;
  transition:color var(--t-rapido) var(--ease), background-color var(--t-rapido) var(--ease);}
.nav-l a:hover{color:var(--text-1); background:var(--surface);}
.nav-l a.on{color:var(--text-1); background:var(--surface-2);}

.nav-m{display:none;}
.nav-m-in{display:flex; flex-direction:column; padding:4px 20px calc(8px + env(safe-area-inset-bottom));}
.nav-m a{font-size:16px; font-weight:450; text-decoration:none; color:var(--text-2);
  padding:13px 0; border-bottom:1px solid var(--border); touch-action:manipulation;
  transition:color var(--t-rapido) var(--ease);}
.nav-m a:last-child{border-bottom:0;}
.nav-m a:hover{color:var(--text-1);}
.nav-m a.on{color:var(--accent-hi);}
.nav-cta{display:flex; align-items:center; gap:8px;}
.hamburguesa{display:none; align-items:center; justify-content:center; width:38px; height:38px;
  flex:0 0 auto; padding:0; border:1px solid var(--border-strong); border-radius:var(--r-sm);
  background:var(--surface); color:var(--text-1); font-size:17px; line-height:1; cursor:pointer;
  touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease), border-color var(--t-rapido) var(--ease);}
.hamburguesa:hover{background:var(--surface-2); border-color:var(--border-accent);}
.hamburguesa[aria-expanded="true"]{background:var(--surface-2); border-color:var(--border-accent);}
.nav-m-backdrop{display:none;}
@media(max-width:900px){
  .nav-l{display:none;}
  .hamburguesa{display:flex;}
  /* El panel entra desplegándose desde la barra: opacidad y un
     translateY corto de 6 px, 200 ms con la curva de salida del sistema.
     Se queda montado siempre (visibility, no display) para que la salida
     también se anime y para no reconstruir el menú en cada toque. */
  .nav-m{display:block; position:fixed; top:64px; right:12px; z-index:59;
    width:min(78vw,260px); background:var(--bg-2); border:1px solid var(--border-strong);
    border-radius:var(--r-md); max-height:calc(100dvh - 64px - 24px); overflow-y:auto;
    overscroll-behavior:contain; box-shadow:var(--glow-md);
    opacity:0; visibility:hidden; transform:translateY(-6px);
    transition:opacity var(--t-medio) var(--ease-out),
               transform var(--t-medio) var(--ease-out),
               visibility 0s linear var(--t-medio);}
  .nav-m.abierto{opacity:1; visibility:visible; transform:translateY(0);
    transition:opacity var(--t-medio) var(--ease-out),
               transform var(--t-medio) var(--ease-out),
               visibility 0s linear 0s;}
  .nav-m-backdrop{display:block; position:fixed; top:64px; left:0; right:0; bottom:0;
    background:rgba(0,0,0,.5); z-index:58; opacity:0; visibility:hidden;
    transition:opacity var(--t-medio) var(--ease-out), visibility 0s linear var(--t-medio);}
  .nav-m-backdrop.abierto{opacity:1; visibility:visible;
    transition:opacity var(--t-medio) var(--ease-out), visibility 0s linear 0s;}
  /* Con el menú de anclas oculto, quedan tres grupos en la barra: logo,
     botones de acción (login + buscar/dar tutorías) y hamburguesa. Se
     achican los botones para que los tres quepan en una sola fila. */
  .nav-in{gap:8px;}
  .nav-in .cambiar{padding:8px 12px; font-size:14px; gap:4px;}
  .nav-in .cambiar i{display:none;}
}
@media(max-width:400px){
  .logo{font-size:17px;}
  .nav-in .cambiar{padding:7px 9px; font-size:13px;}
}

#buscador,#pasos,#dar,#tarifas,#faq,#nosotros,#politicas,#registro{scroll-margin-top:84px;}

/* ---------- BOTONES ---------- */
/* El botón es lo que más se toca en la página, así que su respuesta es
   inmediata y sin movimiento: cambia de superficie en 140 ms y se hunde un
   escalón más al presionar. Nada de levantarse ni escalar — a esta
   frecuencia, un desplazamiento se lee como ruido. */
/* align-items + justify-content centran el contenido en los dos ejes, y
   text-align:center centra además las líneas entre sí cuando la etiqueta se
   parte en dos (pasa en los botones estirados a lo ancho de un modal o de
   una tarjeta angosta). Sin el justify-content, un botón con width:100% o
   flex:1 dejaba su texto pegado a la izquierda. */
.btn{font-family:'Inter',sans-serif; font-weight:500; font-size:15px; line-height:1;
  padding:12px 18px; border-radius:var(--r-sm); border:1px solid var(--border-strong);
  cursor:pointer; background:var(--surface); color:var(--text-1); text-decoration:none;
  display:inline-flex; align-items:center; justify-content:center; text-align:center;
  gap:7px; touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease),
             border-color var(--t-rapido) var(--ease),
             color var(--t-rapido) var(--ease);}
.btn:hover{background:var(--surface-2); border-color:var(--border-accent);}
.btn:active{background:var(--surface-3);}
.btn-p{background:var(--accent); border-color:var(--accent); color:var(--text-inv); font-weight:600;}
.btn-p:hover{background:var(--accent-hover); border-color:var(--accent-hover); color:var(--text-inv);}
.btn-p:active{background:var(--accent-deep); border-color:var(--accent-deep);}
.btn-lg{font-size:16px; padding:14px 22px;}
.btn:disabled{background:var(--surface); border-color:var(--border); color:var(--text-3);
  cursor:not-allowed;}
.btn-azul{background:#0284C7; border-color:#0284C7; color:#fff; font-weight:600;}
.btn-azul:hover{background:#0369A1; border-color:#0369A1; color:#fff;}

/* ---------- HERO ---------- */
/* La portada ya no lleva halo radial de fondo. Los dos que había —uno fijo
   en la esquina y otro detrás del titular— eran decoración pura y son de lo
   primero que delata una página generada. */
.hero{padding:76px 0 56px; position:relative;}

/* El eslogan va en DOS renglones exactos, no tres: cada mitad es su propio
   bloque y el salto queda donde lo pide la frase, no donde caiga el ancho
   de la ventana. max-width en ch sobre el h1 completo dejaba el corte a
   merced del viewport. */
h1.big{font-size:clamp(33px,5.8vw,64px); max-width:none; margin:0; line-height:1.06;
  font-weight:400; letter-spacing:-.035em; text-wrap:balance;}
/* Cada mitad de la frase es su propio bloque, así el corte cae donde lo
   pide el sentido y no donde alcance el ancho. En un teléfono angosto cada
   mitad puede ocupar más de un renglón —a 33 px no cabe de otra forma sin
   dejar el titular en letra de párrafo— pero el corte principal, el que
   separa el costo de la comparación, se respeta siempre. */
h1.big span{display:block;}
h1.big u{text-decoration:none; font-weight:500; color:var(--accent-hi); white-space:nowrap;}
.lede{font-size:clamp(16.5px,1.5vw,18.5px); color:var(--text-2); max-width:56ch; margin:24px 0 0;
  line-height:1.62;}
.lede b{color:var(--text-1); font-weight:500;}
.frase{font-size:16px; color:var(--text-2); max-width:46ch; margin:22px 0 0;
  padding-left:16px; line-height:1.6; border-left:1px solid var(--border-accent);}
.cta{display:flex; gap:10px; flex-wrap:wrap; margin-top:26px;}

/* ---------- BUSCADOR DE PORTADA ----------
   El campo entero se comporta como un control: el foco lo toma el <input>
   pero el borde y el anillo los dibuja el contenedor con :focus-within, que
   es lo que hace que el botón pegado al campo se lea como parte de la misma
   pieza y no como algo suelto al lado. */
.buscador{margin:30px 0 0; max-width:620px;}
.buscador label{display:block; font-family:'IBM Plex Mono',monospace; font-size:12px;
  letter-spacing:.14em; text-transform:uppercase; color:var(--text-3); margin-bottom:9px;}
.campo{display:flex; background:var(--surface); border:1px solid var(--border-strong);
  border-radius:var(--r-md); overflow:hidden;
  transition:border-color var(--t-rapido) var(--ease);}
.campo:focus-within{border-color:var(--accent);
  outline:2px solid rgba(20,184,166,.28); outline-offset:-1px;}
.campo input{flex:1; min-width:0; background:transparent; border:0; color:var(--text-1);
  font-family:'Inter',sans-serif; font-size:16px; padding:14px 16px;}
.campo input:focus{outline:none;}
.campo input::placeholder{color:var(--text-3);}
.campo button{flex:0 0 auto; border:0; border-left:1px solid var(--border);
  background:var(--surface-2); color:var(--text-1); font-family:'Inter',sans-serif;
  font-weight:500; font-size:15px; padding:0 20px; cursor:pointer; touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease), color var(--t-rapido) var(--ease);}
.campo button:hover{background:var(--accent); color:var(--text-inv);}
.campo button:active{background:var(--accent-deep); color:var(--text-inv);}
.hint{margin:10px 0 0; font-size:13.5px; color:var(--text-3); line-height:1.6;}
.hint code{font-family:'IBM Plex Mono',monospace; color:var(--text-2); font-size:13px;}
@media(max-width:480px){
  .campo{flex-wrap:wrap;}
  .campo input{flex:1 0 100%;}
  .campo button{flex:1 0 100%; border-left:0; border-top:1px solid var(--border); padding:13px 20px;}
}

/* ---------- EL CATÁLOGO EN CIFRAS ----------
   Reemplaza la marquesina de códigos que corría bajo la portada. Esa cinta
   se movía sola, en bucle y sin decir nada; estas son las cifras reales del
   catálogo, contadas de datos/areas.js, y cada una es un enlace al
   buscador. Mismo lugar en la página, un trabajo de verdad. */
.cifras{display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:0;
  margin:56px 0 0; border-top:1px solid var(--border-strong);}
/* Todo centrado dentro de su celda —el número y su etiqueta—, y por eso el
   relleno pasa a ser simétrico: con el 26px a la derecha y 0 a la izquierda
   que tenía cuando el texto iba alineado a la izquierda, el centro de la
   caja no coincide con el centro visible y todo queda corrido. */
.cifra{padding:18px 14px 0; text-align:center; text-decoration:none; color:inherit;
  display:block; border-right:1px solid var(--border);}
.cifra:last-child{border-right:0;}
.cifra b{display:block; font-family:'Outfit',sans-serif; font-weight:400;
  font-size:clamp(26px,3vw,34px); line-height:1; letter-spacing:-.04em; color:var(--text-1);
  font-variant-numeric:tabular-nums; transition:color var(--t-rapido) var(--ease);}
.cifra span{display:block; margin-top:9px; font-size:14px; color:var(--text-2); padding-bottom:18px;}
a.cifra:hover b{color:var(--accent-hi);}
@media(max-width:680px){
  .cifras{grid-template-columns:1fr 1fr;}
  .cifra{padding-left:10px; padding-right:10px;}
  .cifra:nth-child(2n){border-right:0;}
}

/* ---------- SECCIONES ---------- */
/* La regla va ARRIBA de cada sección, no abajo: así el pie no queda
   colgando de una línea suelta y la última sección cierra contra el pie. */
.sec{padding:80px 0; border-top:1px solid var(--border);}
h2.big{font-size:clamp(26px,3.6vw,40px); margin:0 0 16px; max-width:22ch; font-weight:400;
  letter-spacing:-.032em; line-height:1.12;}
.sub{color:var(--text-2); max-width:60ch; margin:0 0 32px; font-size:16.5px; line-height:1.65;}
.sub b{color:var(--text-1); font-weight:500;}

/* ---------- INSTITUCIONES ----------
   Nivel superior al de las áreas: agrupa las áreas por institución (Tec,
   Idiomas, y a futuro otras universidades). Mismo lenguaje visual que
   .acard pero más grande, para marcar que es un escalón arriba. */
.instituciones{display:grid; grid-template-columns:repeat(auto-fit,minmax(200px,1fr)); gap:8px;
  margin-bottom:14px;}
/* Estas tarjetas se recorren decenas de veces por visita. Su respuesta al
   puntero es un cambio de superficie de 140 ms y la barra de color que se
   enciende: nada que se levante, escale ni proyecte sombra de color. */
.icard{border:1px solid var(--border); background:var(--surface); border-radius:var(--r-md);
  padding:16px 16px 15px 19px; cursor:pointer; text-align:left; color:var(--text-1);
  position:relative; overflow:hidden; touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease),
             border-color var(--t-rapido) var(--ease);}
.icard:hover{background:var(--surface-2); border-color:var(--border-strong);}
.icard .bar{position:absolute; left:0; top:0; bottom:0; width:2px; background:var(--ac);
  opacity:.55; transition:opacity var(--t-rapido) var(--ease), width var(--t-rapido) var(--ease);}
.icard:hover .bar{opacity:1;}
.icard .ilbl{font-family:'Outfit',sans-serif; font-weight:500; font-size:16.5px; display:block;
  line-height:1.3; letter-spacing:-.02em; color:var(--text-1);}
.icard .idesc{font-family:'IBM Plex Mono',monospace; font-size:12.5px; color:var(--text-3);
  display:block; margin-top:6px; letter-spacing:.02em; font-variant-numeric:tabular-nums;}
.icard[aria-pressed="true"]{background:var(--surface-2); border-color:var(--border-accent);}
.icard[aria-pressed="true"] .bar{opacity:1; width:3px;}
.icard[aria-pressed="true"] .ilbl{color:var(--accent-hi);}
.icard[aria-pressed="true"] .idesc{color:var(--text-2);}

/* ---------- ÁREAS ---------- */
.areas{display:grid; grid-template-columns:repeat(auto-fill,minmax(158px,1fr)); gap:8px;
  margin-bottom:18px;}
.acard{border:1px solid var(--border); background:var(--surface); border-radius:var(--r-sm);
  padding:13px 14px 12px 17px; cursor:pointer; text-align:left; color:var(--text-1);
  position:relative; overflow:hidden; touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease),
             border-color var(--t-rapido) var(--ease);}
.acard:hover{background:var(--surface-2); border-color:var(--border-strong);}
.acard .bar{position:absolute; left:0; top:0; bottom:0; width:2px; background:var(--ac);
  opacity:.5; transition:opacity var(--t-rapido) var(--ease);}
.acard:hover .bar{opacity:1;}
.acard .lbl{font-family:'Outfit',sans-serif; font-weight:400; font-size:15px; display:block;
  line-height:1.3; letter-spacing:-.015em; color:var(--text-1); min-width:0;}
.acard .qty{font-family:'IBM Plex Mono',monospace; font-size:12px; color:var(--text-3);
  display:block; margin-top:5px; letter-spacing:.02em; font-variant-numeric:tabular-nums;}
.acard[aria-pressed="true"]{background:var(--surface-2); border-color:var(--border-accent);}
.acard[aria-pressed="true"] .bar{opacity:1;}
.acard[aria-pressed="true"] .lbl{color:var(--accent-hi);}
.acard[aria-pressed="true"] .qty{color:var(--text-2);}

.cursos{display:flex; flex-wrap:wrap; gap:6px; margin-bottom:24px;}
.cchip{font-family:'IBM Plex Mono',monospace; font-size:12.5px; padding:7px 11px;
  border-radius:var(--r-sm); border:1px solid var(--border); background:var(--surface);
  color:var(--text-2); cursor:pointer; touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease),
             border-color var(--t-rapido) var(--ease),
             color var(--t-rapido) var(--ease);}
.cchip:hover{color:var(--text-1); border-color:var(--border-strong); background:var(--surface-2);}
.cchip[aria-pressed="true"]{background:var(--accent); border-color:var(--accent);
  color:var(--text-inv); font-weight:500;}
/* Cuántos tutores hay ya en ese curso. Va con opacidad y no con un color
   propio, para que se siga leyendo cuando el chip queda seleccionado y el
   texto se invierte sobre el degradado. */
.cchip .cnum{opacity:.68;}

/* "Ver los N cursos restantes": abre las listas largas (las áreas de la UCR
   llegan a más de 200 cursos). No es un curso, así que va punteado y en
   cursiva para que no se lea como uno más de la fila. */
.cmas{border-style:dashed; font-style:italic; color:var(--text-3);}

/* ---------- CONTROLES ---------- */
.ctrl{display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:16px;
  background:var(--bg-2); border:1px solid var(--border); border-radius:var(--r-md);
  padding:18px; margin-bottom:24px;}
.fld label{display:block; font-family:'IBM Plex Mono',monospace; font-size:11.5px;
  letter-spacing:.14em; text-transform:uppercase; color:var(--text-3); margin-bottom:8px;}
.fld select{width:100%; padding:10px 12px; border-radius:var(--r-sm);
  border:1px solid var(--border-strong); background-color:var(--surface); color:var(--text-1);
  font-family:'Inter',sans-serif; font-size:15px; color-scheme:dark;
  transition:border-color var(--t-rapido) var(--ease);}
.fld select:hover{border-color:var(--border-accent);}
.fld select option{background-color:var(--surface); color:var(--text-1);}
.fld input[type=range]{width:100%; accent-color:#14B8A6;}
.count{font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:.1em;
  color:var(--text-3); text-transform:uppercase; margin-bottom:14px;
  font-variant-numeric:tabular-nums;}

/* ---------- TARJETAS DE TUTOR ---------- */
.grid{display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr)); gap:14px;}
.tut{background:var(--surface); border:1px solid var(--border); border-radius:var(--r-md);
  overflow:hidden; display:flex; flex-direction:column;
  transition:background-color var(--t-rapido) var(--ease),
             border-color var(--t-rapido) var(--ease);}
.tut:hover{background:var(--surface-2); border-color:var(--border-strong);}
.tut .strip{height:2px; background:var(--ac); opacity:.55;
  transition:opacity var(--t-rapido) var(--ease);}
.tut:hover .strip{opacity:1;}
.tut .body{padding:18px; display:flex; flex-direction:column; gap:12px; flex:1;}
.top{display:flex; gap:12px; align-items:center; min-width:0;}
/* El NOMBRE se recorta con puntos suspensivos cuando no cabe. Sin
   min-width:0 en el hijo flexible, un nombre largo estira la tarjeta en vez
   de recortarse: el mínimo automático de un elemento flex es el ancho de su
   contenido. */
.top > div{min-width:0;}
.nm{font-family:'Outfit',sans-serif; font-weight:500; font-size:16.5px; line-height:1.25;
  letter-spacing:-.02em; min-width:0; overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap;}
/* La CARRERA no: baja de línea las veces que necesite. Recortarla dejaba
   "Ingeniería en Computación" como "Ingeniería en Comp…" —y a la mitad de
   las carreras del catálogo con el mismo destino—, que es justo el dato por
   el que alguien mira quién le va a dar la clase. Un apellido largo no cabe
   y se entiende igual; una carrera a medias no dice nada.
   overflow-wrap:anywhere para el caso raro de una palabra sola más ancha
   que la tarjeta, que si no la desbordaría. */
.mt{font-size:13px; color:var(--text-3); white-space:normal; overflow-wrap:anywhere;}
.curso-resaltado{color:var(--accent-hi); font-weight:500;}
.bio{font-size:14.5px; color:var(--text-2); flex:1; margin:0; line-height:1.6;}
.stats{display:flex; gap:14px; align-items:center; flex-wrap:wrap; font-size:13.5px;
  color:var(--text-3); font-variant-numeric:tabular-nums;}
.stats .rt{color:var(--text-1); font-family:'IBM Plex Mono',monospace; font-weight:500;}
/* Cuando el tutor todavía no tiene nota ni sesiones, este aviso es lo ÚNICO
   que hay en la fila: ocupa el ancho entero y va centrado, en vez de quedar
   pegado al borde izquierdo con el resto de la fila vacía. La nota y las
   sesiones, cuando existen, no se tocan — son dos datos que se leen en fila
   de izquierda a derecha. */
.stats .nuevo{flex:1 0 100%; text-align:center;}
.star{color:#F59E0B;}
.pr{display:flex; align-items:flex-end; justify-content:space-between; gap:12px;
  flex-wrap:wrap; border-top:1px solid var(--border); padding-top:14px;}
/* Dos botones en una tarjeta de 300 px: se achican para caber juntos, y si
   aun así no caben, bajan a su propia línea en vez de desbordar. */
.tut-acc{display:flex; gap:8px; flex-wrap:wrap;}
.tut-acc .btn{padding:9px 14px; font-size:13.5px;}
.pr .amt{font-family:'Outfit',sans-serif; font-weight:400; font-size:23px; line-height:1;
  letter-spacing:-.035em; color:var(--text-1); font-variant-numeric:tabular-nums;}
.pr .u{font-size:12.5px; color:var(--text-3); display:block; margin-top:6px;}
.empty{border:1px dashed var(--border-strong); border-radius:var(--r-md); padding:48px 24px;
  text-align:center; background:var(--surface);}

/* ---------- MODAL ---------- */
/* La capa entera es la que se desplaza; el contenido se centra por dentro con
   min-height:100%. Así, aunque el diálogo sea más alto que la pantalla, se puede
   recorrer completo y nunca queda una parte fuera de alcance.                  */
/* La capa entra con opacidad y el diálogo con un scale(.97) — nunca desde
   scale(0): nada aparece de la nada. 200 ms con la curva de salida, dentro
   del rango de una ventana modal. Como la capa se monta y se desmonta,
   la entrada va por @keyframes de un solo ciclo, no por transición. */
.ov{position:fixed; inset:0; background:rgba(4,10,12,.82); backdrop-filter:blur(6px);
  z-index:90; overflow-y:auto; overscroll-behavior:contain;
  -webkit-overflow-scrolling:touch; padding:24px 16px;
  animation:capa-entra var(--t-medio) var(--ease-out) both;}
@keyframes capa-entra{from{opacity:0;} to{opacity:1;}}
@keyframes dialogo-entra{from{opacity:0; transform:scale(.97);} to{opacity:1; transform:none;}}
.ov .mdl{animation:dialogo-entra var(--t-medio) var(--ease-out) both;}
.v2.sinmov .ov, .v2.sinmov .ov .mdl{animation:none;}
.ov-caja{min-height:100%; display:flex; align-items:center; justify-content:center;}
.ov-caja > *{width:100%; display:flex; justify-content:center;}
.ov-caja .mdl{margin:0;}
.mdl{background:var(--surface); border:1px solid var(--border-strong); border-radius:var(--r-lg);
  width:100%; max-width:580px; box-shadow:var(--glow-lg); transform-origin:center;}
.mdl-h{padding:26px; border-bottom:1px solid var(--border); display:flex; gap:14px;
  align-items:flex-start; justify-content:space-between; position:sticky; top:-24px;
  background:var(--surface); z-index:3; border-radius:var(--r-lg) var(--r-lg) 0 0;}
.x{background:none; border:none; color:var(--error); font-size:24px; line-height:1;
  cursor:pointer; transition:color .25s var(--ease);}
.x:hover{color:#FCA5A5;}
.mdl-b{padding:26px;}
.cal{display:grid; grid-template-columns:auto repeat(6,1fr); gap:6px;}
.slot{height:36px; border-radius:6px; border:1px solid var(--border); background:var(--surface-2);
  cursor:pointer; font-size:0; transition:var(--tr-superficie);}
.slot:hover{border-color:var(--accent); background:var(--accent-soft);}
.slot.off{background:transparent; border-style:dashed; opacity:.26; cursor:not-allowed;}
.slot.on{background:var(--accent); border-color:var(--accent);}
.recibo{margin-top:24px; border:1px solid var(--border); border-radius:var(--r-md); padding:22px;
  background:var(--bg-2);}
.row{display:flex; justify-content:space-between; font-family:'IBM Plex Mono',monospace;
  font-size:16px; padding:9px 0; color:var(--text-2);}
.row.tot{border-top:1px solid var(--border-strong); margin-top:12px; padding-top:16px;
  color:var(--accent-hi); font-size:18px; font-weight:500;}
.note{font-size:16px; color:var(--text-3); margin:16px 0 0; line-height:1.6;}
.done{background:var(--accent-soft); border:1px solid var(--border-accent); color:var(--text-1);
  border-radius:var(--r-md); padding:18px; font-size:16px; line-height:1.6;}
.expirado{background:rgba(224,177,92,.10); border:1px solid rgba(224,177,92,.4); color:var(--text-1);
  border-radius:var(--r-md); padding:22px; font-size:16px; line-height:1.6;}
.expirado b{color:var(--warning);}

/* ---------- PASOS ---------- */
/* Los pasos sí son una secuencia —buscar, confirmar, pagar— así que la
   numeración se queda: dice algo verdadero del contenido. Lo que se fue es
   la caja: tres tarjetas flotantes con sombra para tres frases cortas era
   envolver el texto en cartón. Ahora es una regla arriba y la columna
   debajo, que es lo que un índice hace. */
.pasos{display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:0;}
.paso{border-top:1px solid var(--border-strong); padding:20px 28px 0 0; background:none;}
.paso .k{font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:.14em;
  text-transform:uppercase; color:var(--accent); display:block; margin-bottom:12px;}
.paso h3{font-family:'Outfit',sans-serif; font-weight:500; font-size:18.5px; margin:0 0 8px;
  letter-spacing:-.02em;}
.paso p{font-size:15px; color:var(--text-2); margin:0; line-height:1.6; max-width:36ch;}

/* ---------- TARIFAS · EL REPARTO ----------
   La regla del ±10 % no se entiende leyéndola, se entiende viéndola: de una
   tarifa salen dos flechas, una que sube y otra que baja, y abajo los tres
   montos que resultan. Es el diagrama de Demos/04-pizarra.html, con su
   misma estructura —la llave que se abre, las dos flechas, la nota del
   redondeo— pero dibujado con la paleta y las tipografías de esta página en
   vez de con marcador sobre pizarra blanca.

   El SVG lleva role="img" y un aria-label que dice las tres cifras: quien
   no ve el dibujo recibe la misma información, no un hueco. */
.reparto{margin-top:32px; border:1px solid var(--border); border-radius:var(--r-md);
  background:var(--surface); padding:28px 24px 24px;}
.reparto svg{width:100%; height:auto; display:block; max-width:660px; margin:0 auto;}
.reparto .trazo{stroke:var(--text-3); stroke-width:1.4; fill:none; stroke-linecap:round;}
.reparto .rot{font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:.12em;
  text-transform:uppercase; fill:var(--text-3);}
.reparto .pct{font-family:'Outfit',sans-serif; font-size:23px; font-weight:500;
  letter-spacing:-.02em;}
.reparto .pct.sube{fill:var(--accent-hi);}
.reparto .pct.baja{fill:var(--accent);}
.reparto .quien{font-family:'Inter',sans-serif; font-size:13px; fill:var(--text-2);}
.reparto .redondeo{font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.08em;
  fill:var(--text-3);}
.rlista{display:grid; grid-template-columns:repeat(3,1fr); gap:0; margin-top:26px;
  border-top:1px solid var(--border);}
@media(max-width:620px){.rlista{grid-template-columns:1fr;}}
/* Mismo trato que .cifra y .stat-card: es el mismo patrón —celdas separadas
   por líneas, con una cifra grande y su explicación— y tiene que leerse
   igual en los tres lados de la página. */
.rc{padding:20px 14px 0; text-align:center; border-right:1px solid var(--border);}
.rc:last-child{border-right:0;}
@media(max-width:620px){
  .rc{border-right:0; border-bottom:1px solid var(--border); padding:16px 0;}
  .rc:last-child{border-bottom:0;}
}
.rc b{display:block; font-family:'Outfit',sans-serif; font-weight:400;
  font-size:clamp(24px,3vw,32px); line-height:1; letter-spacing:-.04em; color:var(--text-1);
  font-variant-numeric:tabular-nums;}
.rc.destaca b{color:var(--accent-hi);}
.rc span{display:block; margin-top:9px; font-size:14px; color:var(--text-2);}
.rc em{display:block; margin-top:3px; font-style:normal; font-family:'IBM Plex Mono',monospace;
  font-size:11.5px; color:var(--text-3); padding-bottom:18px; font-variant-numeric:tabular-nums;}

/* ---------- FORMULARIOS ---------- */
.form{background:var(--surface); border:1px solid var(--border); border-radius:var(--r-lg);
  padding:30px; transition:border-color .3s var(--ease), box-shadow .3s var(--ease);}
.form:hover{border-color:var(--border-accent); box-shadow:var(--glow-sm);}
.form h3{font-family:'Outfit',sans-serif; font-weight:500; font-size:21px; margin:0 0 8px;
  letter-spacing:-.02em; text-transform:none;}
.form .d{font-size:16px; color:var(--text-2); margin:0 0 22px; line-height:1.6;}
.in{width:100%; padding:13px 15px; border-radius:var(--r-sm); border:1px solid var(--border);
  background:var(--bg-2); color:var(--text-1); font-family:'Inter',sans-serif; font-size:16px;
  margin-bottom:14px; transition:border-color .25s var(--ease), box-shadow .25s var(--ease);}
.in::placeholder{color:var(--text-3);}
.in:hover{border-color:var(--border-strong);}
.in:focus{border-color:var(--accent); box-shadow:0 0 0 3px rgba(20,184,166,.14); outline:none;}
.in.ta{resize:vertical; min-height:100px; line-height:1.6;}
.campo-pass{position:relative;}
.campo-pass .in{padding-right:44px;}
.pass-toggle{position:absolute; top:50%; right:6px; transform:translateY(-50%); width:32px; height:32px;
  display:flex; align-items:center; justify-content:center; background:none; border:none; padding:0;
  color:var(--text-3); cursor:pointer; border-radius:var(--r-sm);}
.pass-toggle:hover{color:var(--text-1);}
.pass-toggle svg{width:20px; height:20px;}
.form .lb{display:block; font-family:'IBM Plex Mono',monospace; font-size:16px;
  letter-spacing:.18em; text-transform:uppercase; color:var(--accent); margin:20px 0 12px;}
.contador{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-3);
  text-align:right; margin:0 0 4px;}
.franjas{display:flex; flex-wrap:wrap; gap:7px; margin-bottom:6px;}
.fchip{font-size:14.5px; font-weight:450; padding:9px 14px; border-radius:var(--r-sm);
  border:1px solid var(--border); background:var(--bg-2); color:var(--text-2); cursor:pointer;
  transition:var(--tr-superficie);}
.fchip:hover{border-color:var(--border-accent); color:var(--text-1);}
.fchip[aria-pressed="true"]{background:var(--accent); border-color:var(--accent);
  color:var(--text-inv); font-weight:500;}
.error{background:rgba(248,113,113,.08); border:1px solid rgba(248,113,113,.4);
  color:#FCA5A5; border-radius:var(--r-sm); padding:12px 14px; font-size:16px; margin-top:14px;}
.alerta-archivo{margin:0 0 14px;}
.tel-fila{display:flex; gap:8px;}
.tel-fila .tel-cod{flex:0 0 200px; padding-left:10px; padding-right:6px; overflow:hidden;
  text-overflow:ellipsis; white-space:nowrap; color-scheme:dark;}
.tel-fila .tel-cod option{background:var(--bg-2); color:var(--text-1);}
.tel-fila .tel-cod option:hover,.tel-fila .tel-cod option:focus,
.tel-fila .tel-cod option:checked{background:var(--accent-soft); color:var(--text-1);}
.tel-fila .tel-num{flex:1 1 auto; min-width:0;}
@media(max-width:480px){.tel-fila{flex-wrap:wrap;} .tel-fila .tel-cod{flex:1 1 100%;}}
.nota-correo{font-size:14px; margin-top:-10px;}

/* ---------- DISTINTIVOS ---------- */
.nuevo{color:var(--accent);}

/* ---------- PIE ---------- */
.foot{padding:52px 0 40px; padding-bottom:calc(40px + env(safe-area-inset-bottom));
  font-size:15px; color:var(--text-3); border-top:1px solid var(--border);
  background:var(--bg-2);}
/* ---------- POLÍTICAS ----------
   Las cuatro siguen enteras y numeradas —la numeración es real: se citan por
   número en los correos y en el aviso de aceptación— pero ya no son cuatro
   tarjetas en cuadrícula que se levantan al pasar el puntero. Van una debajo
   de otra, separadas por una regla, con el número de eyebrow y el título
   grande: un documento, que es lo que son. */
.pols{display:grid; grid-template-columns:1fr; gap:0; font-size:16px;}
.pol{background:none; border:0; border-top:1px solid var(--border); border-radius:0;
  padding:40px 0 0; margin-top:40px; display:flex; flex-direction:column;}
.pol:first-child{border-top:0; padding-top:0; margin-top:0;}
.polh{display:flex; align-items:baseline; gap:14px; margin-bottom:14px;}
.pol .pk{font-family:'IBM Plex Mono',monospace; font-size:12px; letter-spacing:.14em;
  color:var(--accent); flex:0 0 auto; font-variant-numeric:tabular-nums;}
.pol h3{font-family:'Outfit',sans-serif; font-weight:400; font-size:clamp(21px,2.4vw,28px);
  margin:0; letter-spacing:-.03em; line-height:1.15; text-wrap:balance;}
.pold{font-size:16.5px; color:var(--text-2); margin:0 0 24px; line-height:1.65; max-width:60ch;}
.pold b{color:var(--text-1); font-weight:500;}
.polnota{border-top:1px solid var(--border); padding-top:14px; margin:20px 0 0 !important;
  font-size:14.5px; color:var(--text-3); line-height:1.55; max-width:70ch;}
.polcita{font-size:16px; color:var(--text-2); line-height:1.65; margin:20px 0;
  padding-left:16px; border-left:1px solid var(--border-accent); max-width:64ch;}

/* Barras de devolución: 10 px, sin el número impreso encima. El porcentaje
   y el monto viven arriba, en la fila de encabezado, donde caben con su
   tamaño real; la barra solo da la proporción. Los tres tramos —lo que se
   devuelve, el 20 % del tutor y el 20 % de Ayudate— se mantienen porque
   dicen a dónde va lo retenido, que es la pregunta que sigue después. */
.barra{background:none; border:0; border-radius:0; padding:0; margin:0 0 14px;
  display:grid; gap:18px; max-width:820px;}
.bfila{margin-bottom:0;}
.bhead{display:flex; justify-content:space-between; align-items:baseline; gap:16px;
  margin-bottom:8px;}
.bcuando{font-size:15.5px; color:var(--text-1);}
.bmonto{font-family:'IBM Plex Mono',monospace; font-size:13.5px; color:var(--text-3);
  font-variant-numeric:tabular-nums; white-space:nowrap;}
.btrack{display:flex; height:10px; border-radius:var(--r-pill); overflow:hidden;
  background:var(--surface-2); border:1px solid var(--border);}
.bfill{background:var(--accent); font-size:0;}
.bfill.full{background:var(--accent-hi);}
.bseg{height:100%;}
.bseg.tu{background:#3F4A63;}
.bseg.ay{background:#2A3347;}
.bleyenda{display:flex; flex-wrap:wrap; gap:16px; margin-top:4px; padding-top:0;
  border-top:0; font-size:13px; color:var(--text-3);}
.bleyenda span{display:inline-flex; align-items:center; gap:7px;}
.sw{width:10px; height:10px; border-radius:3px; display:inline-block; flex:0 0 auto;}
.sw.devuelto{background:var(--accent);}
.sw.tu{background:#3F4A63;}
.sw.ay{background:#2A3347;}

.plist,.check{list-style:none; padding:0; margin:0; display:grid; gap:9px; max-width:76ch;}
.plist li{font-size:15px; color:var(--text-2); line-height:1.55; padding-left:16px;
  position:relative;}
.plist li::before{content:''; position:absolute; left:0; top:8px; width:4px; height:4px;
  border-radius:50%; background:var(--accent);}
.plist li b{color:var(--text-1); font-weight:500;}
.check li{font-size:15px; color:var(--text-2); line-height:1.55; padding-left:22px;
  position:relative;}
.check li b{color:var(--text-1); font-weight:500;}
.check li::before{content:'+'; position:absolute; left:0; top:0; color:var(--accent);
  font-family:'IBM Plex Mono',monospace; font-size:14px;}

.sino{display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:20px; max-width:820px;}
.sino .col{border:1px solid var(--border); border-radius:var(--r-md); padding:20px;
  background:var(--surface);}
.sino .ct{font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.14em;
  text-transform:uppercase; display:block; padding:0; border-radius:0;
  margin-bottom:14px; font-weight:400;}
.sino .si .ct{background:none; color:var(--accent);}
.sino .no .ct{background:none; color:var(--error);}
.sino ul{list-style:none; padding:0; margin:0; display:grid; gap:9px;}
.sino li{font-size:14.5px; color:var(--text-2); line-height:1.45; padding-left:20px;
  position:relative;}
.sino li::before{position:absolute; left:0; top:0; font-family:'IBM Plex Mono',monospace;
  font-size:14px;}
.sino .si li::before{content:'+'; color:var(--accent);}
.sino .no li::before{content:'−'; color:var(--error);}
@media(max-width:660px){.sino{grid-template-columns:1fr;}}

.cancel2{display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:20px; max-width:820px;}
.cancel2 .cbox{border:1px solid var(--border); border-radius:var(--r-md); padding:20px;
  background:var(--surface);}
.cancel2 .ct{font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.14em;
  text-transform:uppercase; display:block; padding:0; border-radius:0;
  margin-bottom:14px; font-weight:400; background:none; color:var(--text-3);}
.cancel2 ul{list-style:none; padding:0; margin:0; display:grid; gap:9px;}
.cancel2 li{font-size:14.5px; color:var(--text-2); line-height:1.5; padding-left:16px;
  position:relative;}
.cancel2 li::before{content:'·'; position:absolute; left:2px; color:var(--text-3);
  font-weight:700;}
@media(max-width:660px){.cancel2{grid-template-columns:1fr;}}

/* El requisito y su cifra: el número grande primero, la explicación debajo.
   Sin caja teñida ni degradado recortado sobre el número — es un dato, no
   un cartel. */
.nota80{display:block; background:none; border:0; border-top:1px solid var(--border-strong);
  border-radius:0; padding:20px 26px 0 0; margin:0 0 8px;}
.n80{font-family:'Outfit',sans-serif; font-weight:400; font-size:34px; line-height:1;
  letter-spacing:-.045em; display:block; margin-bottom:12px; color:var(--accent-hi);
  font-variant-numeric:tabular-nums;}
.n80 sup{font-size:.55em; letter-spacing:0;}
.nota80 b{display:block; font-size:15.5px; color:var(--text-1); font-weight:500;
  margin-bottom:5px;}
.nota80-desc{font-size:14.5px; color:var(--text-2); line-height:1.5; display:block;}
/* Los requisitos van en fila, no apilados: son tres datos comparables. */
.verif{display:grid; grid-template-columns:repeat(auto-fit,minmax(228px,1fr)); gap:0 26px;
  margin-bottom:20px;}

/* Aviso destacado dentro de una tarjeta de política (p. ej. la de cero
   tolerancia al acoso): mismo lenguaje visual que .nota80, en rojo/alerta
   en vez del turquesa del sistema, para que el principio clave resalte
   sin salirse del estilo general de las tarjetas.
   Los textos usan clases propias (.polalerta-ic / .polalerta-desc) en vez
   de un selector genérico ".polalerta span": el ícono TAMBIÉN es un span,
   y un selector así de amplio termina pisándolo (mismo motivo por el que
   se separó .nota80-desc de ".nota80 span" arriba, que se comía el
   tamaño y el color del número grande). */
.polalerta{display:flex; align-items:flex-start; gap:14px; background:rgba(248,113,113,.06);
  border:1px solid rgba(248,113,113,.3); border-radius:var(--r-md); padding:16px 18px;
  margin-bottom:20px; max-width:820px;}
.polalerta-ic{flex:0 0 auto; width:26px; height:26px; border-radius:var(--r-sm);
  background:rgba(248,113,113,.14); color:#FCA5A5; font-family:'IBM Plex Mono',monospace;
  font-size:15px; font-weight:500; line-height:1; display:grid; place-items:center;}
.polalerta b{display:block; font-size:15px; color:#FCA5A5; font-weight:500; letter-spacing:-.01em;}
.polalerta-desc{font-size:14.5px; color:var(--text-2); line-height:1.5; display:block;
  margin-top:4px;}

.elegido{font-size:16px; color:var(--text-2); margin:14px 0 0; text-align:center;}
.elegido b{color:var(--accent-hi);}
.pie{font-size:16px; color:var(--text-3); margin:14px 0 0; text-align:center;}
.pie a{color:var(--accent-hi); text-decoration:underline; text-underline-offset:3px;}

/* ---------- AVISO EMERGENTE DE POLÍTICAS ---------- */
.avw{max-width:520px;}

.avh{display:flex; align-items:center; gap:13px; padding:24px 24px 0;}
.avicon{width:38px; height:38px; flex:0 0 auto; border-radius:11px; display:grid;
  place-items:center; background:rgba(103,232,249,.12); border:1px solid var(--border-accent);
  font-family:'Outfit',sans-serif; font-weight:600; font-size:19px; color:var(--accent-hi);}
.avh h4{font-family:'Outfit',sans-serif; font-weight:500; font-size:19px; margin:0;
  letter-spacing:-.02em;}
.avb{padding:20px 24px 24px;}
.avtexto{font-size:16px; color:var(--text-2); line-height:1.68; margin:0 0 18px;
  padding-left:16px; border-left:2px solid var(--accent-line);}
.avtexto b{color:var(--text-1);}
.avcheck{display:flex; align-items:flex-start; gap:11px; background:var(--bg-2);
  border:1px solid var(--border); border-radius:var(--r-md); padding:14px 16px; cursor:pointer;
  transition:border-color .25s var(--ease);}
.avcheck:hover{border-color:var(--border-accent);}
.avcheck input{margin:2px 0 0; width:17px; height:17px; accent-color:#14B8A6; flex:0 0 auto;
  cursor:pointer;}
.avcheck span{font-size:16px; color:var(--text-1); line-height:1.5;}
.avacc{display:flex; gap:10px; margin-top:18px;}
.avacc .btn{flex:1; text-align:center;}

/* ---------- FORMULARIO DE TUTOR A ANCHO COMPLETO ---------- */
.form.ancho{max-width:none;}
.lineas{display:grid; grid-template-columns:1fr 1fr; gap:10px; margin:4px 0 22px;}
.lchip{text-align:left; padding:14px 16px; border-radius:var(--r-md);
  border:1px solid var(--border); background:var(--bg-2); color:var(--text-2);
  cursor:pointer; transition:var(--tr-superficie);}
.lchip b{display:block; font-size:16px; color:var(--text-1); margin-bottom:3px; font-weight:600;}
.lchip span{font-size:16px; line-height:1.4; display:block;}
.lchip:hover{border-color:var(--border-accent);}
.lchip[aria-pressed="true"]{background:var(--accent-soft); border-color:var(--accent);
  box-shadow:var(--glow-sm);}
.lchip[aria-pressed="true"] b{color:var(--accent-hi);}

.fcols{display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1.15fr); gap:26px;}
@media(max-width:900px){.fcols{grid-template-columns:1fr;} .lineas{grid-template-columns:1fr;}}
.fcol{min-width:0;}
.fcol .lb:first-child{margin-top:0;}
/* En escritorio, la columna de disponibilidad queda fija en su lugar
   mientras la izquierda (el detalle de cada oferta, que suele ser más
   larga) se desplaza normalmente con el resto de la página. align-self:start
   es lo que le da a la columna su propio alto (el del contenido) en vez de
   estirarse parejo con la izquierda — sin eso no le queda recorrido para
   pegarse. A propósito SIN max-height ni scroll propio: la grilla debe verse
   fija de un solo vistazo, no dentro de su propia barra de desplazamiento.
   Se desactiva por completo en celular (ver el max-width:900px de arriba,
   que ya vuelve todo una sola columna). */
@media(min-width:901px){
  .fcols > .fcol:last-child{position:sticky; top:84px; align-self:start;}
}


.notas{background:var(--bg-2); border:1px solid var(--border); border-left:2px solid var(--accent);
  border-radius:var(--r-sm); padding:12px 14px; margin-bottom:12px;}
.notas p{margin:0 0 7px; font-size:16px; color:var(--text-2); line-height:1.55;}
.notas p:last-child{margin-bottom:0;}
.notas b{color:var(--text-1);}

/* ---------- AVISOS DEL FORMULARIO ---------- */
.privado{margin-top:16px; padding:12px 14px; border-radius:var(--r-sm); background:var(--bg-2);
  border:1px solid var(--border); border-left:2px solid var(--accent);
  font-size:16px; color:var(--text-2); line-height:1.55;}
.privado b{color:var(--text-1);}

/* ---------- CUENTA BANCARIA DEL TUTOR (FormTutor.jsx) ---------- */
.cuentab{margin-top:16px; padding:16px 16px 4px; border-radius:var(--r-md); background:var(--bg-2);
  border:1px solid var(--border); border-left:2px solid var(--accent);}
.cuentab .lb:first-child{margin-top:0;}

/* ---------- GRILLA DE DISPONIBILIDAD ---------- */
.gwrap{border:1px solid var(--border); border-radius:var(--r-md); background:var(--bg-2);
  padding:12px;}
.grid-h{display:grid; gap:3px; user-select:none; touch-action:none;}
.gdd{font-family:'IBM Plex Mono',monospace; font-size:16px; letter-spacing:.08em;
  color:var(--text-3); text-align:center; padding-bottom:5px;}
.ghh{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-3);
  display:flex; align-items:center; justify-content:flex-end; padding-right:7px;}
.gcell{position:relative;}
.gcel{width:100%; height:21px; border-radius:4px; border:1px solid var(--border);
  background:var(--mod-off); cursor:pointer; font-family:'IBM Plex Mono',monospace;
  font-size:16px; font-weight:600; color:var(--text-3); padding:0;
  transition:var(--tr-superficie);}
.gcel:hover{border-color:var(--mod-virt);}
.gcel.virtual{background:var(--mod-virt); border-color:var(--mod-virt); color:#061633;}

.gleg{display:flex; align-items:center; flex-wrap:wrap;
  gap:10px 14px; margin-top:11px; padding-top:10px;
  border-top:1px solid var(--border); font-size:16px; color:var(--text-3);}
.gleg .limpiar{margin-left:auto;}
.gleg-items{display:flex; flex-wrap:wrap; gap:14px;}
.gleg span{display:inline-flex; align-items:center; gap:6px;}
.gsw{width:11px; height:11px; border-radius:3px; display:inline-block; flex:0 0 auto;
  border:1px solid var(--border);}
.gsw.virtual{background:var(--mod-virt); border-color:var(--mod-virt);}
.gsw.vac{background:var(--mod-off); border-color:var(--border-strong);}

/* ---------- CALENDARIO DE LA SOLICITUD ---------- */
.calh{display:flex; align-items:center; justify-content:space-between; margin-bottom:12px;}
.calh span{font-family:'Outfit',sans-serif; font-size:16px; font-weight:500; color:var(--text-1);
  text-transform:capitalize; letter-spacing:-.01em;}
.cnav{width:32px; height:32px; border-radius:8px; border:1px solid var(--border);
  background:var(--surface-2); color:var(--text-2); font-size:17px; line-height:1; cursor:pointer;
  transition:var(--tr-superficie);}
.cnav:hover{border-color:var(--accent); color:var(--accent-hi);}
.calm{display:grid; grid-template-columns:repeat(7,1fr); gap:5px;}
.cdd{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-3);
  text-align:center; padding-bottom:4px;}
.cdia{height:36px; border-radius:8px; border:1px solid transparent; background:transparent;
  color:var(--text-3); font-family:'Inter',sans-serif; font-size:16px; cursor:pointer;
  transition:var(--tr-superficie);}
.cdia.hay{background:var(--surface-2); border-color:var(--border); color:var(--text-1);}
.cdia.hay:hover{border-color:var(--accent); background:var(--accent-soft);}
.cdia:disabled{cursor:not-allowed; opacity:.3;}
.cdia.on{background:var(--accent); border-color:var(--accent); color:var(--text-inv);
  font-weight:600;}
.cleyenda{font-size:16px; color:var(--text-3); margin:14px 0 0; line-height:1.55;}

.hchips{display:flex; flex-wrap:wrap; gap:10px;}
.hchip{border:1px solid var(--border); border-radius:var(--r-sm); background:var(--surface-2);
  padding:11px 15px; cursor:pointer; text-align:left; transition:var(--tr-superficie);}
.hchip b{display:block; font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-1);
  font-weight:600;}
.hchip span{display:block; font-size:16px; color:var(--text-3); margin-top:4px;
  letter-spacing:.04em;}
.hchip:hover{border-color:var(--accent);}
.hchip.on{background:var(--accent); border-color:var(--accent);}
.hchip.on b, .hchip.on span{color:var(--text-inv);}
.hchip.reservado{cursor:not-allowed; opacity:.55; background:var(--surface-2);}
.hchip.reservado:hover{border-color:var(--border);}
.hchip.reservado b{color:var(--text-3);}
.hres{display:block; font-size:13px; font-weight:700; letter-spacing:.05em; text-transform:uppercase;
  color:var(--error); margin-top:4px;}

/* ---------- CONTACTO Y USO DE DATOS ---------- */
.contacto{display:flex; align-items:center; justify-content:space-between; gap:20px;
  flex-wrap:wrap; background:var(--bg-2); border:1px solid var(--border-accent);
  border-radius:var(--r-lg); padding:20px 24px; margin-bottom:14px;}
.contacto b{display:block; font-size:16px; color:var(--text-1); font-weight:600; margin-bottom:4px;}
.contacto span{font-size:16px; color:var(--text-2);}
.foot-links{display:flex; gap:10px; flex-wrap:wrap;}
.fcanal{display:inline-flex; align-items:center; gap:9px; text-decoration:none;
  font-size:16px; color:var(--text-2); border:1px solid var(--border);
  border-radius:var(--r-sm); padding:10px 15px; background:var(--surface);
  transition:var(--tr-superficie);}
.fcanal:hover{color:var(--text-1); border-color:var(--border-accent); background:var(--surface-2);}
.fcanal.fuerte{background:var(--accent); border-color:var(--accent); color:var(--text-inv);
  font-weight:600;}
.fcanal.fuerte:hover{background:var(--grad-hover); color:var(--text-inv);}
.fic{width:15px; height:15px; flex:0 0 auto; display:inline-block; background:currentColor;
  -webkit-mask-repeat:no-repeat; mask-repeat:no-repeat;
  -webkit-mask-size:contain; mask-size:contain;}
.fic.wa{-webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.16c-.25.69-1.43 1.32-1.98 1.4-.53.08-1.19.11-1.92-.12-.44-.14-1.01-.33-1.74-.64-3.06-1.32-5.06-4.4-5.21-4.6-.15-.2-1.25-1.66-1.25-3.17 0-1.51.79-2.25 1.07-2.56.28-.31.61-.39.82-.39.2 0 .41 0 .59.01.19.01.44-.07.69.53.25.6.86 2.11.94 2.26.08.15.13.33.03.53-.1.2-.15.33-.3.5-.15.18-.31.39-.45.53-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.35 1.46.29.15.46.13.63-.08.17-.2.73-.85.92-1.14.19-.29.39-.24.65-.15.26.1 1.66.78 1.94.93.28.15.47.22.54.34.07.13.07.72-.18 1.41z'/%3E%3C/svg%3E");
  mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.8 14.16c-.25.69-1.43 1.32-1.98 1.4-.53.08-1.19.11-1.92-.12-.44-.14-1.01-.33-1.74-.64-3.06-1.32-5.06-4.4-5.21-4.6-.15-.2-1.25-1.66-1.25-3.17 0-1.51.79-2.25 1.07-2.56.28-.31.61-.39.82-.39.2 0 .41 0 .59.01.19.01.44-.07.69.53.25.6.86 2.11.94 2.26.08.15.13.33.03.53-.1.2-.15.33-.3.5-.15.18-.31.39-.45.53-.15.15-.3.31-.13.6.17.29.76 1.25 1.63 2.03 1.12 1 2.06 1.31 2.35 1.46.29.15.46.13.63-.08.17-.2.73-.85.92-1.14.19-.29.39-.24.65-.15.26.1 1.66.78 1.94.93.28.15.47.22.54.34.07.13.07.72-.18 1.41z'/%3E%3C/svg%3E");}
.fic.mail{-webkit-mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4.24-8 4.5-8-4.5V6l8 4.5L20 6v2.24z'/%3E%3C/svg%3E");
  mask-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 4.24-8 4.5-8-4.5V6l8 4.5L20 6v2.24z'/%3E%3C/svg%3E");}

.datos{font-size:16px; color:var(--text-3); line-height:1.6; margin:0 0 20px;
  padding-left:14px; border-left:2px solid var(--border-strong); max-width:78ch;}
.datos b{color:var(--text-2);}

/* ---------- PIE ---------- */
.foot-top{display:flex; align-items:flex-start; justify-content:space-between; gap:32px;
  flex-wrap:wrap; padding-bottom:0; border-bottom:0;}
.foot-cont{display:flex; flex-direction:column; gap:12px;}
.foot-lb{font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.13em;
  text-transform:uppercase; color:var(--text-3);}
.indep{font-size:13px; color:var(--text-3); line-height:1.6; margin:0; max-width:88ch;}
.indep b{color:var(--text-2); font-weight:500;}

/* ==================================================================
   ACCESIBILIDAD
================================================================== */
.sr-only{position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden;
  clip:rect(0 0 0 0); white-space:nowrap; border:0;}

/* enlace de salto: invisible hasta recibir foco */
.salto{position:fixed; top:-100px; left:16px; z-index:200; background:var(--accent);
  color:var(--text-inv); font-weight:600; font-size:15px; padding:12px 20px;
  border-radius:var(--r-sm); text-decoration:none;
  transition:top var(--t-medio) var(--ease-out); box-shadow:var(--glow-md);}
.salto:focus{top:16px;}

/* botón flotante */
.a11y-btn{position:fixed; right:20px; bottom:20px; z-index:120; width:52px; height:52px;
  border-radius:50%; border:1px solid var(--accent); background:var(--accent);
  color:var(--text-inv); cursor:pointer; display:grid; place-items:center;
  box-shadow:var(--glow-md); touch-action:manipulation;
  transition:background-color var(--t-rapido) var(--ease), border-color var(--t-rapido) var(--ease);}
.a11y-btn:hover{background:var(--accent-hover); border-color:var(--accent-hover);}
.a11y-btn svg{width:28px; height:28px;}
.a11y-badge{position:absolute; top:-3px; right:-3px; min-width:20px; height:20px; padding:0 5px;
  border-radius:10px; background:var(--surface); color:var(--accent-hi); font-size:16px;
  font-weight:700; display:grid; place-items:center; border:1px solid var(--border-accent);
  font-family:'IBM Plex Mono',monospace;}
@media(max-width:560px){.a11y-btn{right:14px; bottom:14px; width:48px; height:48px;}
  .a11y-btn svg{width:25px; height:25px;}}

/* panel */
.a11y-panel{position:fixed; right:20px; bottom:86px; z-index:121; width:330px;
  max-height:calc(100vh - 120px); overflow-y:auto; background:var(--surface);
  border:1px solid var(--border-strong); border-radius:var(--r-lg); box-shadow:var(--glow-lg);}
@media(max-width:560px){.a11y-panel{right:10px; left:10px; width:auto; bottom:76px;}}
.a11y-h{display:flex; align-items:center; justify-content:space-between; padding:18px 20px 14px;
  border-bottom:1px solid var(--border); position:sticky; top:0; background:var(--surface); z-index:2;}
.a11y-h h4{font-family:'Outfit',sans-serif; font-weight:500; font-size:18px; margin:0;
  letter-spacing:-.02em; color:var(--text-1);}
.a11y-b{padding:16px 20px 20px;}
.a11y-grp{margin-bottom:20px;}
.a11y-lb{display:block; font-family:'IBM Plex Mono',monospace; font-size:16px; letter-spacing:.18em;
  text-transform:uppercase; color:var(--accent); margin-bottom:10px;}
.a11y-nota{font-size:16px; color:var(--text-3); line-height:1.5; margin:9px 0 0;}

.a11y-seg{display:grid; grid-template-columns:repeat(4,1fr); gap:5px;}
.a11y-seg button{padding:9px 0; border-radius:var(--r-sm); border:1px solid var(--border);
  background:var(--bg-2); color:var(--text-2); font-family:'IBM Plex Mono',monospace;
  font-size:16px; cursor:pointer; transition:var(--tr-superficie);}
.a11y-seg button:hover{border-color:var(--border-accent); color:var(--text-1);}
.a11y-seg button[aria-pressed="true"]{background:var(--accent); border-color:var(--accent);
  color:var(--text-inv); font-weight:600;}

.a11y-list{display:flex; flex-direction:column; gap:5px;}
.a11y-op{text-align:left; padding:10px 13px; border-radius:var(--r-sm);
  border:1px solid var(--border); background:var(--bg-2); color:var(--text-2); cursor:pointer;
  transition:var(--tr-superficie);}
.a11y-op b{display:block; font-size:16px; color:var(--text-1); font-weight:600;}
.a11y-op span{display:block; font-size:16px; margin-top:2px;}
.a11y-op:hover{border-color:var(--border-accent);}
.a11y-op.on{background:var(--accent-soft); border-color:var(--accent);}
.a11y-op.on b{color:var(--accent-hi);}

.a11y-sw{display:flex; align-items:flex-start; gap:12px; width:100%; text-align:left;
  padding:11px 13px; border-radius:var(--r-sm); border:1px solid var(--border);
  background:var(--bg-2); cursor:pointer; margin-bottom:6px; transition:var(--tr-superficie);}
.a11y-sw:hover{border-color:var(--border-accent);}
.a11y-sw.on{background:var(--accent-soft); border-color:var(--accent);}
.a11y-swt{flex:0 0 auto; width:34px; height:19px; border-radius:10px; background:var(--surface-3);
  border:1px solid var(--border-strong); position:relative; margin-top:1px;
  transition:var(--tr-superficie);}
.a11y-swt i{position:absolute; top:2px; left:2px; width:13px; height:13px; border-radius:50%;
  background:var(--text-3);
  transition:transform var(--t-rapido) var(--ease-out), background-color var(--t-rapido) var(--ease);}
.a11y-sw.on .a11y-swt{background:var(--accent); border-color:var(--accent);}
.a11y-sw.on .a11y-swt i{transform:translateX(15px); background:var(--text-inv);}
.a11y-swl b{display:block; font-size:16px; color:var(--text-1); font-weight:600;}
.a11y-swl span{display:block; font-size:16px; color:var(--text-3); margin-top:2px; line-height:1.45;}

/* ---- Efectos de los ajustes ---- */
.v2.z70 .contenido, .v2.z70 .mdl{zoom:.70;}
.v2.z80 .contenido, .v2.z80 .mdl{zoom:.80;}
.v2.z90 .contenido, .v2.z90 .mdl{zoom:.90;}
.v2.z110 .contenido, .v2.z110 .mdl{zoom:1.10;}
.v2.z125 .contenido, .v2.z125 .mdl{zoom:1.25;}
.v2.z150 .contenido, .v2.z150 .mdl{zoom:1.50;}
.v2.z175 .contenido, .v2.z175 .mdl{zoom:1.75;}
/* El zoom de CSS rompe position:sticky en el navegador: sin esto, la barra
   de navegación deja de pegarse arriba en cuanto la persona elige cualquiera
   de los niveles de zoom de arriba. */
.nav, .nav-m{zoom:1;}

.v2.f-protanopia .contenido, .v2.f-protanopia .ov{filter:url(#ay-protanopia);}
.v2.f-deuteranopia .contenido, .v2.f-deuteranopia .ov{filter:url(#ay-deuteranopia);}
.v2.f-tritanopia .contenido, .v2.f-tritanopia .ov{filter:url(#ay-tritanopia);}
.v2.f-grises .contenido, .v2.f-grises .ov{filter:url(#ay-grises);}

.v2.fuente-legible, .v2.fuente-legible *{font-family:'Atkinson Hyperlegible','Inter',sans-serif !important;}
.v2.fuente-legible .mono, .v2.fuente-legible .clista b, .v2.fuente-legible .cchip,
.v2.fuente-legible .eye{letter-spacing:.02em !important;}

.v2.espaciado p, .v2.espaciado li, .v2.espaciado .bio, .v2.espaciado .sub,
.v2.espaciado .lede, .v2.espaciado .d, .v2.espaciado .mt{
  line-height:1.75 !important; letter-spacing:.06em !important; word-spacing:.16em !important;}
.v2.espaciado p{margin-bottom:1.6em !important;}

.v2.contraste{--text-1:#FFFFFF; --text-2:#E2E8F0; --text-3:#CBD5E1;
  --bg:#000000; --bg-2:#080A10; --surface:#101014; --surface-2:#16161C; --surface-3:#1E1E26;
  --border:rgba(255,255,255,.30); --border-strong:rgba(255,255,255,.48);
  --border-accent:rgba(103,232,249,.6); --accent:#2DD4BF; --accent-hi:#A5F3FC;}
.v2.contraste .note, .v2.contraste .a11y-nota, .v2.contraste .cleyenda{color:var(--text-2);}

.v2.enlaces a:not(.btn):not(.a11y-btn){text-decoration:underline !important;
  text-underline-offset:3px; text-decoration-thickness:2px;}
.v2.enlaces .btn, .v2.enlaces .fcanal, .v2.enlaces .cchip, .v2.enlaces .acard, .v2.enlaces .icard,
.v2.enlaces .lchip, .v2.enlaces .fchip, .v2.enlaces .hchip{
  outline:2px solid var(--accent-hi); outline-offset:2px;}
.v2.enlaces .nav-l a::before{content:"› ";}

.v2.sinmov *, .v2.sinmov *::before, .v2.sinmov *::after{
  animation:none !important; transition:none !important; scroll-behavior:auto !important;}

/* ---------- BANDERA DE ESTADO DEL TUTOR ---------- */
/* Solo hay una bandera: la de pendiente. La de "aprobado" se fue con su
   estilo — en el catálogo público toda tarjeta lo está. */
.flag{align-self:flex-start; display:inline-flex; align-items:center; gap:7px;
  font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.13em;
  text-transform:uppercase; font-weight:400; padding:4px 9px; border-radius:var(--r-sm);}
.flag i{width:7px; height:7px; border-radius:50%; flex:0 0 auto;}
.flag.pendiente{background:rgba(248,113,113,.09); color:#FCA5A5;
  border:1px solid rgba(248,113,113,.35);}
.flag.pendiente i{background:#F87171;}
.tut.pend{opacity:.9;}
.tut.pend .strip{background:#F87171 !important; opacity:.75;}
.tut.pend:hover{border-color:rgba(248,113,113,.4); box-shadow:0 10px 34px rgba(248,113,113,.14);}
.tut.pend .amt{color:var(--text-3);}
.pendnota{font-size:16px; color:#FCA5A5; line-height:1.5; margin:0;
  padding:9px 11px; border-radius:var(--r-sm); background:rgba(248,113,113,.07);
  border-left:2px solid rgba(248,113,113,.5);}

.oblig{font-size:16px; color:var(--text-3); text-align:center; margin:12px 0 0;}

/* ---------- BUSCADOR DE CURSOS CON SUGERENCIAS ---------- */
.bc{position:relative; margin-bottom:4px;}
.bc .in{margin-bottom:0;}
.bcsug{position:absolute; top:calc(100% + 5px); left:0; right:0; z-index:25; list-style:none;
  margin:0; padding:5px; background:var(--surface); border:1px solid var(--border-strong);
  border-radius:var(--r-sm); box-shadow:var(--glow-md); max-height:265px; overflow-y:auto;}
.bcop{display:flex; align-items:center; gap:11px; width:100%; text-align:left; padding:8px 10px;
  border:none; border-radius:5px; background:transparent; cursor:pointer;
  transition:var(--tr-superficie);}
.bcop.on, .bcop:hover{background:var(--accent-soft);}
.bccod{font-family:'IBM Plex Mono',monospace; font-size:16px; font-weight:600; padding:3px 8px;
  border-radius:4px; flex:0 0 auto; color:var(--ac); border:1px solid var(--ac);
  background:rgba(255,255,255,.03);}
.bcnom{font-size:16px; color:var(--text-1); line-height:1.35;}
.bcnom i{display:block; font-style:normal; font-size:16px; color:var(--text-3); margin-top:2px;}
.bcsel{display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;}
.bcchip{display:inline-flex; align-items:center; gap:7px; font-size:16px; color:var(--text-2);
  background:var(--bg-2); border:1px solid var(--border); border-left:2px solid var(--ac);
  border-radius:5px; padding:5px 6px 5px 10px; line-height:1.3;}
.bcchip b{font-family:'IBM Plex Mono',monospace; color:var(--ac); font-weight:600;}
.bcchip button{background:none; border:none; color:var(--error); font-size:16px; line-height:1;
  cursor:pointer; padding:0 3px; transition:color .2s var(--ease);}
.bcchip button:hover{color:#FCA5A5;}

.bcotro{opacity:.82;}
.bcmas{font-family:'IBM Plex Mono',monospace; font-size:16px; font-weight:600; width:26px;
  height:22px; display:grid; place-items:center; border-radius:4px; flex:0 0 auto;
  color:var(--text-3); border:1px dashed var(--border-strong);}
.bcotro:hover .bcmas{color:var(--accent-hi); border-color:var(--accent);}

/* ---------- CAMPOS SIN COMPLETAR ---------- */
@keyframes faltaPulso{
  0%{box-shadow:0 0 0 0 rgba(248,113,113,.45);}
  100%{box-shadow:0 0 0 7px rgba(248,113,113,0);}
}
.in.falta, .in.falta:hover{
  border-color:#F87171 !important;
  background:rgba(248,113,113,.06);
  animation:faltaPulso .7s ease-out 1;
}
.in.falta:focus{border-color:#F87171 !important;
  box-shadow:0 0 0 3px rgba(248,113,113,.18) !important;}
.in.falta::placeholder{color:#FCA5A5;}
.falta-bloque{border:1px solid #F87171; border-radius:var(--r-md); padding:8px;
  background:rgba(248,113,113,.05); animation:faltaPulso .7s ease-out 1;}
.v2.sinmov .in.falta, .v2.sinmov .falta-bloque{animation:none;}


/* día con horas ya elegidas */
.cdia.elegido{background:var(--accent-soft); border-color:var(--accent); color:var(--accent-hi);
  font-weight:600; position:relative;}
.cdia.elegido.on{background:var(--accent); border-color:var(--accent); color:var(--text-inv);}
.cnum{position:absolute; top:-5px; right:-5px; min-width:16px; height:16px; padding:0 4px;
  border-radius:8px; background:var(--accent); color:var(--text-inv);
  font-family:'IBM Plex Mono',monospace; font-size:16px; font-weight:700; font-style:normal;
  display:grid; place-items:center; border:1px solid var(--surface);}

/* reserva temporal de 5 minutos sobre las horas elegidas */
.reserva-temp{margin-top:18px; display:flex; align-items:center; gap:10px;
  border:1px solid rgba(224,177,92,.4); background:rgba(224,177,92,.10);
  border-radius:var(--r-md); padding:12px 16px; font-size:15px; line-height:1.5;
  color:var(--text-2);}
.reserva-temp b{font-family:'IBM Plex Mono',monospace; color:var(--warning); font-size:16px;}
.reserva-punto{width:8px; height:8px; border-radius:50%; background:var(--warning); flex:0 0 auto;
  animation:reserva-pulso 1.6s ease-in-out infinite;}
@keyframes reserva-pulso{0%,100%{opacity:1;} 50%{opacity:.35;}}
.v2.sinmov .reserva-punto{animation:none;}

/* resumen de días y horas */
.resumen{margin-top:22px; border:1px solid var(--border-accent); border-radius:var(--r-md);
  background:var(--accent-soft); padding:16px 18px;}
.resh{display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding-bottom:12px; margin-bottom:10px; border-bottom:1px solid var(--border);
  font-size:16px; color:var(--text-2);}
.resh b{color:var(--accent-hi);}
.resh button{background:none; border:none; color:var(--text-3); font-size:16px; cursor:pointer;
  text-decoration:underline; text-underline-offset:3px; padding:0;}
.resh button:hover{color:var(--accent-hi);}
.resumen ul{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:9px;}
.resumen li{display:flex; align-items:center; gap:10px; font-size:16px; flex-wrap:wrap;}
.rfecha{color:var(--text-1); font-weight:600; flex:0 0 auto; min-width:132px;}
.rhoras{font-family:'IBM Plex Mono',monospace; color:var(--text-2); font-size:16px; flex:1;}
.resumen li button{background:none; border:none; color:var(--error); font-size:16px; line-height:1;
  cursor:pointer; padding:0 3px;}
.resumen li button:hover{color:#FCA5A5;}

/* políticas en ventana propia */
.ov-alta{z-index:130;}
.pol-mdl{max-width:600px;}
.pol-b section{padding-bottom:20px; margin-bottom:20px; border-bottom:1px solid var(--border);}
.pol-b section:last-of-type{border-bottom:none;}
.pm-n{font-family:'IBM Plex Mono',monospace; font-size:16px; letter-spacing:.16em;
  color:var(--accent); display:block; margin-bottom:7px;}
.pol-b h5{font-family:'Outfit',sans-serif; font-weight:500; font-size:17px; margin:0 0 12px;
  letter-spacing:-.02em; color:var(--text-1);}
.pol-b .check li{font-size:16px;}
.pm-cita{font-size:16px; color:var(--text-2); line-height:1.65; margin:0 0 12px;
  padding-left:14px; border-left:2px solid var(--accent-line); font-style:italic;}
.pm-datos{font-size:16px; color:var(--text-3); line-height:1.6; margin:0 0 20px;
  padding:12px 14px; border-radius:var(--r-sm); background:var(--bg-2);
  border:1px solid var(--border);}
.pm-datos b{color:var(--text-2);}
.enlace-pol{background:none; border:none; padding:0; font:inherit; cursor:pointer;
  color:var(--accent-hi); text-decoration:underline; text-underline-offset:3px;}
.enlace-pol:hover{color:var(--text-1);}
.enlace-login{background:none; border:none; padding:0; font:inherit; cursor:pointer;
  color:#0284C7; text-decoration:underline; text-underline-offset:3px;}
.enlace-login:hover{color:#0369A1;}

/* ---------- CHIP DE DISPONIBILIDAD DE USUARIO (ver Mensajes.jsx) ---------- */
.chip{display:inline-block; font-size:13px; font-weight:600; padding:3px 10px; border-radius:var(--r-pill);}
.chip.off{background:rgba(148,163,184,.15); color:var(--text-2);}
.chip.danger{background:rgba(248,113,113,.15); color:#FCA5A5;}
.chip.success{background:rgba(45,212,191,.16); color:var(--success,#2DD4BF);}

/* ---------- NOTAS DE AYUDA BAJO LOS CAMPOS ---------- */
.ayuda{font-size:16px; line-height:1.5; color:var(--text-3); margin:2px 0 16px; padding-left:2px;}
.ayuda.mal{color:#FCA5A5; font-weight:500;}
.ayuda.mal::before{content:"▲ "; font-size:16px; vertical-align:1px;}
.contador.corto{color:#FCA5A5; font-weight:600;}

/* ---------- LISTA DE ERRORES ---------- */
.error{background:rgba(248,113,113,.09); border:1px solid rgba(248,113,113,.45); color:#FCA5A5;
  border-radius:var(--r-sm); padding:14px 16px; font-size:16px; margin-top:14px; line-height:1.6;}
.error b{display:block; color:#FECACA; margin-bottom:8px; font-size:16px;}
.error ul{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:6px;}
.error li{padding-left:16px; position:relative; font-size:16px;}
.error li::before{content:"·"; position:absolute; left:4px; font-weight:700; font-size:16px;
  line-height:1;}

/* ---------- LISTADO COMPACTO DE CURSOS EN LA TARJETA ---------- */
.clista{list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:3px;}
.clista li{display:flex; align-items:baseline; gap:7px; font-size:16px; line-height:1.35;
  min-width:0;}
.clista li b{font-family:'IBM Plex Mono',monospace; font-size:16px; font-weight:600;
  color:var(--ac); flex:0 0 auto; letter-spacing:.02em;}
.clista li span{color:var(--text-2); overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap; min-width:0;}
.clista .cmas{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-3);
  padding-left:1px;}

/* ---------- PAGINACIÓN ---------- */
.pag{display:flex; align-items:center; justify-content:center; gap:6px; flex-wrap:wrap;
  margin-top:28px;}
.pbtn{min-width:38px; height:38px; padding:0 11px; border-radius:var(--r-sm);
  border:1px solid var(--border); background:var(--surface); color:var(--text-2);
  font-family:'Inter',sans-serif; font-size:16px; cursor:pointer;
  transition:var(--tr-superficie);}
.pbtn:hover:not(:disabled){border-color:var(--accent); color:var(--text-1);
  background:var(--surface-2);}
.pbtn:disabled{opacity:.32; cursor:not-allowed;}
.pbtn.on{background:var(--accent); border-color:var(--accent); color:var(--text-inv);
  font-weight:600;}
.psalto{color:var(--text-3); padding:0 3px; font-size:16px;}
.ordnota{font-size:16px; color:var(--text-2); line-height:1.6; margin:-4px 0 18px;
  padding:14px 16px; border-left:3px solid var(--accent); border-radius:var(--r-sm);
  background:var(--accent-soft); max-width:70ch;}

/* ---------- AVATAR ---------- */
.av{position:relative; flex:0 0 auto; border-radius:var(--r-sm); overflow:visible;
  background:linear-gradient(140deg,rgba(6,182,212,.20),rgba(20,184,166,.12));
  border:1px solid var(--border-accent); color:var(--accent-hi);
  display:grid; place-items:center;}
.av img{width:100%; height:100%; object-fit:cover; border-radius:calc(var(--r-sm) - 1px);
  display:block;}
.avic{width:62%; height:62%; opacity:.85;}
.av.esperando{background:rgba(248,113,113,.10); border-color:rgba(248,113,113,.45);
  color:#FCA5A5;}
.avesp{position:absolute; right:-5px; bottom:-5px; width:19px; height:19px; border-radius:50%;
  background:var(--surface); border:1px solid rgba(248,113,113,.6); color:#F87171;
  display:grid; place-items:center;}
.avesp svg{width:12px; height:12px;}

/* ---------- FOTO EN EL FORMULARIO ---------- */
.opc{font-family:'IBM Plex Mono',monospace; letter-spacing:.1em; text-transform:uppercase;
  font-size:12px; font-weight:700; color:#5EEAD4; background:rgba(45,212,191,.16);
  display:inline-block; padding:2px 9px; border-radius:99px; vertical-align:middle;
  margin-left:6px;}
.fotorow{display:flex; gap:16px; align-items:flex-start; margin-bottom:16px;}
.fotoprev{width:78px; height:78px; flex:0 0 auto; border-radius:var(--r-md); overflow:hidden;
  background:var(--bg-2); border:1px dashed var(--border-strong); color:var(--text-3);
  display:grid; place-items:center;}
.fotoprev img{width:100%; height:100%; object-fit:cover;}
.fotoprev svg{width:44%; height:44%;}
.fotoacc{flex:1; min-width:0;}
.fotobtn{display:inline-block; cursor:pointer; padding:9px 16px; font-size:16px;}
.fotobtn input{display:none;}
.fotoquitar{background:none; border:none; color:var(--text-3); font-size:16px; cursor:pointer;
  text-decoration:underline; text-underline-offset:3px; margin-left:12px;}
.fotoquitar:hover{color:#FCA5A5;}

/* ---------- COMPROBANTE POR CURSO, EN EL FORMULARIO DE TUTOR ---------- */
.comprow{display:flex; gap:12px; align-items:center; flex-wrap:wrap; margin-top:2px;}
.compprev{width:56px; height:56px; flex:0 0 auto; border-radius:var(--r-sm); overflow:hidden;
  background:var(--bg-2); border:1px solid var(--border-strong);}
.compprev img{width:100%; height:100%; object-fit:cover;}

/* ---------- ESQUELETOS DE CARGA ---------- */
@keyframes brillo{0%{background-position:-260px 0;}100%{background-position:260px 0;}}
.esq{background:var(--surface); border:1px solid var(--border); border-radius:var(--r-lg);
  padding:22px; display:flex; flex-direction:column; gap:10px;}
.esq i, .esq-av{display:block; height:11px; border-radius:5px;
  background:linear-gradient(90deg,var(--surface-2) 0%,var(--surface-3) 45%,var(--surface-2) 90%);
  background-size:520px 100%; animation:brillo 1.25s linear infinite;}
.esq-av{width:46px; height:46px; border-radius:var(--r-sm); flex:0 0 auto;}
.esq-top{display:flex; gap:14px; align-items:center; margin-bottom:6px;}
.esq-top span{display:flex; flex-direction:column; gap:7px;}
.esq-pr{display:flex; align-items:center; justify-content:space-between; margin-top:auto;
  padding-top:14px; border-top:1px solid var(--border);}
.v2.sinmov .esq i, .v2.sinmov .esq-av{animation:none;}

/* ---------- BOTÓN ENVIANDO ---------- */
@keyframes gira{to{transform:rotate(360deg);}}
.spin{display:inline-block; width:14px; height:14px; margin-right:9px; vertical-align:-2px;
  border:2px solid rgba(4,33,31,.3); border-top-color:var(--text-inv); border-radius:50%;
  animation:gira .7s linear infinite;}
.v2.sinmov .spin{animation:none;}

/* ---------- BARRA DE CARGA (envío del registro de tutor) ----------
   Un solo ciclo de 0% a 100%, sin loop: el envío sube imágenes en base64
   dentro del mismo POST y no hay forma de medir el avance real con
   fetch(), así que FormTutor.jsx anima el ancho a mano (por estado, no por
   keyframes) acercándose a un techo sin llegar del todo mientras espera la
   respuesta, y recién salta a 100% en el instante exacto en que el
   servidor confirma el envío — sincronizado con la pantalla de éxito que
   aparece justo después. El ancho lo pone el propio elemento (style
   inline); acá solo va la transición que lo anima. */
.carga{position:relative; width:100%; height:7px; margin-top:16px; border-radius:999px;
  background:var(--surface-2); border:1px solid var(--border); overflow:hidden;}
.carga-fill{position:absolute; top:-1px; bottom:-1px; left:0; width:0%; border-radius:999px;
  background:var(--accent); transition:width .35s var(--ease-out);}
.carga-nota{display:block; text-align:center; font-size:13px; color:var(--text-3);
  margin-top:10px; letter-spacing:.02em;}
.v2.sinmov .carga-fill{transition:none;}

/* ---------- PREGUNTAS FRECUENTES ----------
   Ni cajas ni bordes por pregunta: una lista con una regla entre renglones,
   que es como se lee un índice de preguntas. El botón sigue siendo un
   <button> con aria-expanded y aria-controls; lo único que cambió es cómo
   se ve y cómo se abre.

   La apertura anima grid-template-rows de 0fr a 1fr, no la altura: el
   navegador interpola la fracción de la pista y la respuesta baja sola,
   sin que haya que medirla ni fijarle un alto. La respuesta se queda
   montada siempre para que el cierre también se anime — se le quita del
   alcance del lector de pantalla y del tabulador con visibility:hidden,
   que se conmuta al final de la transición al cerrar y al principio al
   abrir. */
.faq{display:flex; flex-direction:column; border-top:1px solid var(--border); max-width:820px;}
.faqi{border-bottom:1px solid var(--border); background:none; border-radius:0;}
.faqi h3{margin:0; font-size:inherit; font-weight:inherit;}
.faqi button{display:flex; align-items:flex-start; justify-content:space-between; gap:20px;
  width:100%; text-align:left; padding:17px 0; background:none; border:none; cursor:pointer;
  font-family:'Outfit',sans-serif; font-size:17.5px; font-weight:500; color:var(--text-1);
  letter-spacing:-.02em; line-height:1.35; touch-action:manipulation;
  transition:color var(--t-rapido) var(--ease);}
.faqi button:hover{color:var(--accent-hi);}
.faqi.on button{color:var(--accent-hi);}
/* La cruz gira 45° para volverse una equis al abrir: el mismo trazo, no un
   glifo distinto que aparece de golpe. */
.faqi button i{font-style:normal; font-family:'IBM Plex Mono',monospace; font-size:19px;
  color:var(--text-3); flex:0 0 auto; line-height:1.2; display:block;
  transition:transform var(--t-medio) var(--ease-out), color var(--t-rapido) var(--ease);}
.faqi.on button i{transform:rotate(45deg); color:var(--accent);}
.faqr{display:grid; grid-template-rows:0fr; visibility:hidden;
  transition:grid-template-rows var(--t-medio) var(--ease-out),
             opacity var(--t-medio) var(--ease-out),
             visibility 0s linear var(--t-medio);
  opacity:0;}
.faqi.on .faqr{grid-template-rows:1fr; opacity:1; visibility:visible;
  transition:grid-template-rows var(--t-medio) var(--ease-out),
             opacity var(--t-medio) var(--ease-out),
             visibility 0s linear 0s;}
.faqr > div{min-height:0; overflow:hidden;}
.faqr p{margin:0 0 20px; font-size:15.5px; color:var(--text-2); line-height:1.7; max-width:66ch;}

/* ---------- QUIÉNES SOMOS ---------- */
.nos{display:grid; grid-template-columns:1.1fr .9fr; gap:44px; align-items:start;}
@media(max-width:820px){.nos{grid-template-columns:1fr; gap:32px;}}
.nos > div > p{font-size:16.5px; color:var(--text-2); line-height:1.75; margin:0 0 16px;
  max-width:60ch;}
.nos > div > p b{color:var(--text-1); font-weight:500;}
.noscols{display:flex; flex-direction:column;}
.noscard{background:none; border:0; border-top:1px solid var(--border); border-radius:0;
  padding:18px 0;}
.nosk{font-family:'IBM Plex Mono',monospace; font-size:11.5px; letter-spacing:.13em;
  text-transform:uppercase; color:var(--text-3); display:block; margin-bottom:6px;}
.noscard b{display:block; font-family:'Outfit',sans-serif; font-weight:500; font-size:17px;
  color:var(--text-1); margin-bottom:8px; letter-spacing:-.02em;}
.noscard p{margin:0; font-size:14.5px; color:var(--text-2); line-height:1.6;}

.fotobtn.ocupado{opacity:.6; pointer-events:none;}

/* ---- Encabezado de la disponibilidad con su botón de limpiar ---- */
.displb{display:flex; align-items:center; justify-content:space-between; gap:12px; margin:0 0 10px;}
.limpiar{background:none; border:none; color:var(--text-3); font-size:16px; cursor:pointer;
  text-decoration:underline; text-underline-offset:3px; padding:0;}
.limpiar:hover{color:#FCA5A5;}

/* ---- Notas por curso ---- */
.notaf{display:flex; align-items:center; gap:12px; margin-bottom:8px;}
.notan{flex:1; min-width:0; display:flex; align-items:baseline; gap:8px; font-size:16px;}
.notan b{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--ac); flex:0 0 auto;}
.notan em{font-style:normal; color:var(--text-2); overflow:hidden; text-overflow:ellipsis;
  white-space:nowrap;}
.notai{width:104px; flex:0 0 auto; margin:0 !important; text-align:center;}

.notai.nivel{width:150px; text-align:left;}

/* ==================================================================
   IDENTIDAD POR PÁGINA
   La principal (estudiantes) va en cian; la de tutores se corre hacia
   el verde azulado del botón secundario, para que se note de una que
   son dos espacios distintos sin romper la paleta.
================================================================== */
.v2.pag-tutores{
  --accent:#0D9488;
  --accent-2:#14B8A6;
  --accent-hi:#5EEAD4;
  --accent-hover:#2DD4BF;
  --accent-soft:rgba(13,148,136,.12);
  --accent-line:rgba(45,212,191,.30);
  --border-accent:rgba(45,212,191,.34);
  --grad:linear-gradient(135deg,#14B8A6 0%,#0D9488 100%);
  --grad-hover:linear-gradient(135deg,#2DD4BF 0%,#14B8A6 100%);
  --glow-sm:0 2px 12px rgba(13,148,136,.20);
  --glow-md:0 10px 32px rgba(13,148,136,.26);
  --glow-lg:0 28px 74px rgba(13,148,136,.22);
  --text-inv:#022C26;
}
.v2.pag-tutores::before{
  background:radial-gradient(closest-side, rgba(13,148,136,.15), rgba(45,212,191,.05) 55%, transparent 76%);}
.v2.pag-tutores .hero::after{
  background:radial-gradient(closest-side, rgba(13,148,136,.18), rgba(20,184,166,.07) 52%, transparent 78%);}

/* Enlace para saltar de una página a la otra */
.cambiar{display:inline-flex; align-items:center; gap:7px; text-decoration:none;
  font-size:14.5px; font-weight:500; color:var(--text-1); border:1px solid var(--border-strong);
  border-radius:var(--r-sm); padding:10px 16px; background:var(--surface);
  transition:var(--tr-superficie); white-space:nowrap; touch-action:manipulation;}
.cambiar:hover{background:var(--surface-2); border-color:var(--border-accent);
  color:var(--accent-hi);}
.cambiar:active{background:var(--surface-3);}
.cambiar i{font-style:normal; font-size:14px; color:var(--accent);
  transition:transform var(--t-rapido) var(--ease-out);}
@media(hover:hover) and (pointer:fine){
  .cambiar:hover i{transform:translateX(2px);}
  .cambiar.volver:hover i{transform:translateX(-2px);}
}
@media(max-width:560px){.cambiar{padding:9px 13px; font-size:14px;}}


.flujo{list-style:none; counter-reset:f; margin:14px 0 0; padding:0; display:flex;
  flex-direction:column; gap:9px;}
.flujo li{counter-increment:f; position:relative; padding-left:30px; font-size:16px;
  line-height:1.6; color:var(--text-2);}
.flujo li::before{content:counter(f); position:absolute; left:0; top:1px; width:20px; height:20px;
  border-radius:50%; background:var(--accent-soft); border:1px solid var(--border-accent);
  color:var(--accent-hi); font-family:'IBM Plex Mono',monospace; font-size:16px;
  font-weight:600; display:grid; place-items:center;}
.flujo li b{color:var(--text-1);}
.flujonota{display:block; margin-top:14px; font-size:16px; color:var(--text-3);}

.hmodo{display:inline-flex; align-items:center; gap:5px;}
.hmodo::before{content:""; width:7px; height:7px; border-radius:2px; flex:0 0 auto;
  background:var(--mod-virt);}
.hchip.on .hmodo::before{outline:1px solid rgba(255,255,255,.5);}

/* Encabezado de las páginas sueltas: sin el resplandor ni la cadena de códigos */
.hero.corto{padding:56px 0 24px;}
h1.anton.chico{font-size:clamp(30px,4.4vw,46px); max-width:20ch; margin:0;
  font-weight:400; letter-spacing:-.035em; line-height:1.08;}
.hero.corto .lede{margin:20px 0 0;}


.foot-nav{display:flex; flex-wrap:wrap; gap:20px; margin:34px 0 22px; padding-top:22px;
  border-top:1px solid var(--border); font-size:14.5px;}
.foot-nav a{color:var(--text-2); text-decoration:none;
  transition:color var(--t-rapido) var(--ease);}
.foot-nav a:hover{color:var(--text-1);}
.vacio-acc{display:flex; gap:10px; justify-content:center; flex-wrap:wrap;}

/* ---- Presentación del botón de accesibilidad ---- */
@keyframes latir{0%,100%{box-shadow:0 0 0 0 rgba(20,184,166,.28);}50%{box-shadow:0 0 0 9px rgba(20,184,166,0);}}
.a11y-btn.llamando{animation:latir 2.4s ease-in-out infinite;}
.v2.sinmov .a11y-btn.llamando{animation:none;}
.a11y-tip{position:fixed; right:86px; bottom:22px; z-index:119; max-width:280px;
  background:var(--surface); border:1px solid var(--border-accent); border-radius:var(--r-md);
  padding:15px 17px; box-shadow:var(--glow-md);}
.a11y-tip b{display:block; font-size:16px; color:var(--text-1); margin-bottom:6px;}
.a11y-tip span{display:block; font-size:16px; color:var(--text-2); line-height:1.55;}
.a11y-tipx{margin-top:12px; background:none; border:none; padding:0; cursor:pointer;
  font-size:16px; color:var(--accent-hi); text-decoration:underline; text-underline-offset:3px;}
.a11y-tip::after{content:""; position:absolute; right:-7px; bottom:22px; width:12px; height:12px;
  background:var(--surface); border-right:1px solid var(--border-accent);
  border-top:1px solid var(--border-accent); transform:rotate(45deg);}
@media(max-width:700px){
  .a11y-tip{right:14px; left:14px; max-width:none; bottom:76px;}
  .a11y-tip::after{display:none;}
}
.a11y-sub{margin:3px 0 0; font-size:16px; color:var(--text-3);}

/* ---- Grilla en celular: un día a la vez ---- */
.gdias{display:flex; gap:5px; overflow-x:auto; padding-bottom:10px; margin-bottom:12px;
  border-bottom:1px solid var(--border); scrollbar-width:none;}
.gdias::-webkit-scrollbar{display:none;}
.gdia{position:relative; flex:0 0 auto; min-width:52px; padding:11px 12px; border-radius:var(--r-sm);
  border:1px solid var(--border); background:var(--surface); color:var(--text-2);
  font-family:'IBM Plex Mono',monospace; font-size:16px; letter-spacing:.06em; cursor:pointer;}
.gdia.on{background:var(--accent); border-color:var(--accent); color:var(--text-inv); font-weight:600;}
.gdia i{position:absolute; top:-5px; right:-5px; min-width:17px; height:17px; padding:0 4px;
  border-radius:9px; background:var(--accent-hi); color:var(--text-inv); font-style:normal;
  font-size:16px; font-weight:700; display:grid; place-items:center; border:1px solid var(--surface);}
.ghoras{display:flex; flex-direction:column; gap:6px;}
.ghora{display:flex; align-items:center; justify-content:space-between; gap:12px; width:100%;
  min-height:48px; padding:11px 15px; border-radius:var(--r-sm); border:1px solid var(--border);
  background:var(--mod-off); cursor:pointer; text-align:left; transition:var(--tr-superficie);}
.ghora:hover{border-color:var(--border-strong);}
.ghh2{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--text-1); font-weight:500;}
.ghv{font-size:16px; color:var(--text-3);}
.ghora.virtual{background:var(--mod-virt); border-color:var(--mod-virt);}
.ghora.virtual .ghh2,.ghora.virtual .ghv{color:#061633;}

.aviso24{display:block; font-size:16px; color:var(--text-2); line-height:1.6; margin:16px 0 0;
  padding:14px 16px; border-radius:var(--r-sm); background:var(--accent-soft);
  border:1px solid var(--border-accent);}
.aviso24 b{color:var(--accent-hi);}

/* ---- Precio por curso en el formulario ---- */
.cursosf{display:flex; flex-direction:column; gap:10px; margin-bottom:4px;}
.cursof{border:1px solid var(--border); border-radius:var(--r-md); background:var(--bg-2);
  padding:14px 16px; position:relative;}
.cursof-x{position:absolute; top:10px; right:10px; width:24px; height:24px; border-radius:50%;
  border:1px solid rgba(248,113,113,.4); background:rgba(248,113,113,.08); color:#FCA5A5;
  font-size:15px; line-height:1; cursor:pointer; display:grid; place-items:center;
  transition:var(--tr-superficie);}
.cursof-x:hover{background:rgba(248,113,113,.18); border-color:#FCA5A5;}
.cursoft{display:flex; align-items:baseline; gap:9px; margin-bottom:11px; font-size:16px;
  padding-right:32px;}
.cursoft b{font-family:'IBM Plex Mono',monospace; font-size:16px; color:var(--ac); flex:0 0 auto;}
.cursoft em{font-style:normal; color:var(--text-1); font-weight:500;}
.cursofc{display:grid; grid-template-columns:1fr 1fr; gap:10px;}
/* Las materias de colegio no piden nota, así que en su tarjeta queda un
   solo campo —la tarifa— y ocupa el ancho entero en vez de dejar media
   fila vacía al lado. */
.cursofc.una{grid-template-columns:1fr;}
.cursofc label span{display:block; font-size:14px; color:var(--text-3); margin-bottom:5px;}
.cursofc .in{margin:0;}
.cursofp{margin:10px 0 0; font-size:14px; color:var(--text-3);}
.cursofp b{color:var(--accent-hi);}
.cursofp.sinreq{margin-top:14px; padding:11px 13px; border-radius:var(--r-sm);
  background:var(--surface); border:1px solid var(--border);
  border-left:2px solid var(--accent); color:var(--text-2); line-height:1.55;}
.cursofp.sinreq b{color:var(--text-1); font-weight:500;}
@media(max-width:520px){.cursofc{grid-template-columns:1fr;}}

/* ---- Elegir curso dentro de la solicitud ---- */
.cursosel{display:flex; flex-wrap:wrap; gap:7px; margin-bottom:18px;}
.cursosel .cchip{display:flex; flex-direction:column; align-items:flex-start; gap:3px;
  font-family:'Inter',sans-serif; font-size:14.5px; padding:9px 13px;}
.cursosel .cchip em{font-style:normal; font-family:'IBM Plex Mono',monospace; font-size:12.5px;
  opacity:.85;}
.cursosel .cchip.on{background:var(--ac); border-color:var(--ac); color:#04211F; font-weight:600;}
.desde{font-style:normal; font-size:16px; color:var(--text-3); margin-right:6px;
  font-family:"Inter",sans-serif; font-weight:400;}

/* ---- El curso, encabezando la tarjeta ----
   Dos renglones: arriba, en mono y pequeño, el código y dónde se imparte —
   los datos con los que se identifica un curso—; abajo, su nombre, que es
   lo que se lee. La nota del tutor no está ni puede estar: es privada. */
.cursotit{display:block; min-width:0;}
.cursotit-meta{display:flex; align-items:center; gap:9px; flex-wrap:wrap; margin-bottom:7px;
  font-family:'IBM Plex Mono',monospace; font-size:12px; color:var(--text-3);
  letter-spacing:.03em;}
.cursotit-meta b{font-family:'IBM Plex Mono',monospace; font-size:12.5px; font-weight:500;
  color:var(--ac); flex:0 0 auto; letter-spacing:.04em;}
.cursotit h3{font-family:'Outfit',sans-serif; font-weight:500; font-size:19px; margin:0;
  color:var(--text-1); letter-spacing:-.022em; line-height:1.25;
  transition:color var(--t-rapido) var(--ease);}
.cursotit.activo h3{color:var(--accent-hi);}
.nivelt{font-family:'IBM Plex Mono',monospace; font-size:11.5px; padding:2px 7px;
  border-radius:4px; background:var(--accent-soft); border:1px solid var(--border-accent);
  color:var(--accent-hi); flex:0 0 auto; letter-spacing:.04em;}

.cursofd{display:block; margin-top:12px;}
.cursofd > span{display:block; font-size:16px; color:var(--text-3); margin-bottom:5px;}
.cursofd .in{margin:0;}
.cursofd i{display:block; font-style:normal; font-family:'IBM Plex Mono',monospace;
  font-size:12px; color:var(--text-3); text-align:right; margin-top:4px;}
.cursofd .comprow + i{text-align:left; line-height:1.55;}
.cursofd i.corto{color:#FCA5A5; font-weight:600;}

/* ==================================================================
   LA ENTRADA DEL ENCABEZADO
   El único momento orquestado de la página, y ocurre una vez por carga:
   etiqueta, titular, bajada, frase y botones suben 10 px y aparecen, con
   60 ms de diferencia entre uno y otro. Es el tramo de "raro / primera
   vez", que es donde cabe un gesto así; en todo lo demás —tarjetas,
   chips, filtros, que se ven decenas de veces por visita— el movimiento
   sería ruido y no lo hay.

   Solo transform y opacity, ease-out, 420 ms. Va por @keyframes y no por
   transición porque no hay estado que conmutar: es predeterminado, se
   dispara al montar y corre fuera del hilo principal mientras el resto de
   la página todavía está cargando el catálogo.
   ================================================================== */
@keyframes entra{
  from{opacity:0; transform:translateY(10px);}
  to{opacity:1; transform:none;}
}
.hero .wrap > *{animation:entra var(--t-entrada) var(--ease-out) both;}
.hero .wrap > *:nth-child(1){animation-delay:0ms;}
.hero .wrap > *:nth-child(2){animation-delay:60ms;}
.hero .wrap > *:nth-child(3){animation-delay:120ms;}
.hero .wrap > *:nth-child(4){animation-delay:180ms;}
.hero .wrap > *:nth-child(5){animation-delay:240ms;}
.hero .wrap > *:nth-child(n+6){animation-delay:300ms;}
/* Las cifras son el segundo bloque de la portada y viven en su propio
   .wrap, así que sin esto entrarían a la vez que la etiqueta de arriba.
   Cierran la secuencia. */
.hero .wrap > .cifras{animation-delay:300ms;}
.v2.sinmov .hero .wrap > *{animation:none;}

/* Movimiento reducido: menos y más suave, no cero. Se van los
   desplazamientos —la entrada del encabezado, el panel del menú, el
   diálogo que escala— y se quedan las transiciones de color y opacidad,
   que son las que dicen si un botón respondió o si una respuesta se
   abrió. */
@media(prefers-reduced-motion:reduce){
  .v2 .hero .wrap > *{animation:none;}
  .v2 .ov .mdl{animation:capa-entra var(--t-medio) var(--ease-out) both;}
  .v2 .nav-m{transition:opacity var(--t-medio) var(--ease-out),
                        visibility 0s linear var(--t-medio);
    transform:none;}
  .v2 .nav-m.abierto{transform:none;
    transition:opacity var(--t-medio) var(--ease-out), visibility 0s linear 0s;}
  .v2 .cambiar:hover i, .v2 .faqi.on button i{transform:none;}
  .v2 .esq i, .v2 .esq-av, .v2 .spin, .v2 .reserva-punto,
  .v2 .a11y-btn.llamando{animation:none !important;}
  .v2 *{scroll-behavior:auto !important;}
}

/* El puntero fino es el único que tiene :hover de verdad. En una pantalla
   táctil, un toque dispara el hover y lo deja pegado hasta el toque
   siguiente, así que el movimiento del hover se limita a donde hay ratón. */
@media(hover:none){
  .v2 .cambiar:hover i{transform:none;}
}
`;
